import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger 
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  LayoutDashboard, Users, FileText, Settings, Shield, 
  TrendingUp, MessageCircle, Plus, Loader2, Trash2,
  PenSquare, BarChart3, ChevronRight, Tag, Cog, Mail, X,
  Ban, VolumeX, Flag, Eye, ExternalLink, Check, ShieldAlert, ShieldOff, AlertTriangle, Search, Moon, Crown, Palette, Upload, Image, KeyRound, RefreshCw, Clock, UserCheck
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import { API_URL } from '@/lib/api';
const PUBLIC_URL = process.env.PUBLIC_URL || '';

const resolveThemeImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('/api/')) return `${API_URL}${imageUrl}`;
  if (imageUrl.startsWith('/')) return `${PUBLIC_URL}${imageUrl}`;
  return imageUrl;
};

const getDisplayedUserRole = (user) => user.premium_role_name || user.role;

const getRoleBadgeClass = (user) => {
  if (user.premium_role_name === 'Quiet Mode') return 'bg-blue-600 text-white border-blue-500';
  if (user.premium_role_name === 'After Hours') return 'bg-purple-600 text-white border-purple-500';
  if (user.premium_role_name === 'Needlessly Rich') return 'bg-yellow-500 text-yellow-950 border-yellow-400';
  if (user.role === 'admin' || user.role === 'superadmin') return 'bg-forest-800 text-white';
  return '';
};

const getDistributionBadgeClass = (role) => {
  if (role === 'Quiet Mode') return 'bg-blue-600 text-white border-blue-500';
  if (role === 'After Hours') return 'bg-purple-600 text-white border-purple-500';
  if (role === 'Needlessly Rich') return 'bg-yellow-500 text-yellow-950 border-yellow-400';
  if (role === 'admin' || role === 'superadmin') return 'bg-forest-800 text-white border-forest-700';
  return '';
};

// Sidebar Navigation
const AdminNav = ({ currentPath, allowedTabs }) => {
  const [badgeCounts, setBadgeCounts] = useState({ pending_reports: 0, pending_approvals: 0, unverified_users: 0 });

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/badge-counts`);
        setBadgeCounts(res.data);
      } catch (e) {}
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    return () => clearInterval(interval);
  }, []);

  const allNavItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Overview', exact: true, tab: 'overview' },
    { path: '/admin/posts', icon: FileText, label: 'Posts', tab: 'posts' },
    { path: '/admin/topics', icon: Tag, label: 'Topics', tab: 'topics' },
    { path: '/admin/users', icon: Users, label: 'Users', tab: 'users', badge: badgeCounts.unverified_users },
    { path: '/admin/roles', icon: Shield, label: 'Roles', tab: 'roles' },
    { path: '/admin/badges', icon: Crown, label: 'Badges', tab: 'badges' },
    { path: '/admin/themes', icon: Palette, label: 'Themes', tab: 'themes' },
    { path: '/admin/approvals', icon: MessageCircle, label: 'Approvals', tab: 'approvals', badge: badgeCounts.pending_approvals },
    { path: '/admin/reports', icon: Flag, label: 'Reports', tab: 'reports', badge: badgeCounts.pending_reports },
    { path: '/admin/security', icon: ShieldAlert, label: 'Security', tab: 'security', badge: badgeCounts.tamper_events },
    { path: '/admin/settings', icon: Cog, label: 'Settings', tab: 'settings' }];

  const navItems = allNavItems.filter(item => allowedTabs.includes(item.tab));

  return (
    <nav className="space-y-1">
      {navItems.map(item => {
        const isActive = item.exact 
          ? currentPath === item.path 
          : currentPath.startsWith(item.path);
        
        return (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              isActive 
                ? 'bg-forest-800 text-white' 
                : 'hover:bg-muted text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`admin-nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium flex-1">{item.label}</span>
            {item.badge > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5" data-testid={`badge-${item.tab}`}>
                {item.badge}
              </span>
            )}
            {isActive && !item.badge && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Link>
        );
      })}
    </nav>
  );
};

// Overview Dashboard
const OverviewTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/analytics`);
        setAnalytics(response.data);
      } catch (error) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div>
        <h2 className="font-serif text-2xl font-bold">Dashboard Overview</h2>
        <p className="text-muted-foreground">Monitor your community's activity</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-3xl font-bold">{analytics?.users?.total || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-forest-700 dark:text-forest-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              +{analytics?.users?.new_this_week || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Posts</p>
                <p className="text-3xl font-bold">{analytics?.content?.posts || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-gold" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              +{analytics?.content?.new_posts_week || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Comments</p>
                <p className="text-3xl font-bold">{analytics?.content?.comments || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              +{analytics?.content?.new_comments_week || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Verified Users</p>
                <p className="text-3xl font-bold">{analytics?.users?.verified || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {analytics?.users?.premium || 0} premium
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Role Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>User Role Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {Object.entries(analytics?.role_distribution || {}).map(([role, count]) => (
              <div key={role} className="flex items-center gap-2">
                <Badge variant="secondary" className={getDistributionBadgeClass(role)}>
                  {role}
                </Badge>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Posts Management
const PostsTab = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [newPost, setNewPost] = useState({ content: '', image_url: '', topic_id: '', is_filtered: false, is_archived: false, premium_role: '', ad_ineligible: false });
  const [creating, setCreating] = useState(false);
  const [filterType, setFilterType] = useState('main'); // 'main', 'filtered', or 'archives'
  const [showNewTopicInput, setShowNewTopicInput] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [roles, setRoles] = useState([]);

  const resetPostForm = () => {
    setNewPost({ content: '', image_url: '', topic_id: '', is_filtered: false, is_archived: false, premium_role: '', ad_ineligible: false });
    setShowNewTopicInput(false);
    setNewTopicName('');
  };

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/topics`);
      setTopics((response.data.topics || []).sort((a, b) => (b.post_count || 0) - (a.post_count || 0)));
    } catch (error) {
      console.error('Failed to fetch topics');
    }
  };

  const fetchPosts = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts?limit=50&is_filtered=${filterType === 'filtered'}&is_archived=${filterType === 'archives'}`);
      setPosts(response.data.posts);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchTopics();
    axios.get(`${API_URL}/api/roles`).then(res => setRoles(res.data.roles || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchPosts();
  }, [fetchPosts]);

  const createInlineTopic = async (topicName) => {
    const trimmedTopicName = topicName.trim();
    if (!trimmedTopicName) return '';
    const response = await axios.post(
      `${API_URL}/api/topics`,
      { name: trimmedTopicName },
    );
    setTopics((prev) => {
      const nextTopics = [...prev, response.data];
      return nextTopics.sort((a, b) => (b.post_count || 0) - (a.post_count || 0));
    });
    setNewPost((p) => ({ ...p, topic_id: response.data.topic_id }));
    setNewTopicName('');
    setShowNewTopicInput(false);
    return response.data.topic_id;
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) {
      toast.error('Content is required');
      return;
    }
    
    // Strip wrapping quotation marks from copy/paste artifacts
    let cleanContent = newPost.content.trim();
    const openQuotes = '"\u201c\u201e';
    const closeQuotes = '"\u201d\u201f';
    if (cleanContent.length >= 2 && openQuotes.includes(cleanContent[0]) && closeQuotes.includes(cleanContent[cleanContent.length - 1])) {
      cleanContent = cleanContent.slice(1, -1);
    }
    
    setCreating(true);
    try {
      let topicId = newPost.topic_id || null;
      if (showNewTopicInput && newTopicName.trim()) {
        topicId = await createInlineTopic(newTopicName);
      }

      const postData = {
        content: cleanContent,
        image_url: newPost.image_url || null,
        topic_id: topicId,
        is_filtered: newPost.is_filtered,
        is_archived: newPost.is_archived,
        premium_role: newPost.premium_role || null,
        ad_ineligible: !!newPost.ad_ineligible
      };
      await axios.post(
        `${API_URL}/api/posts`,
        postData,
        
      );
      toast.success('Post created successfully!');
      resetPostForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setNewPost({
      content: post.content,
      image_url: post.image_url || '',
      topic_id: post.topic_id || '',
      is_filtered: post.is_filtered || false,
      is_archived: post.is_archived || false,
      premium_role: post.premium_role || '',
      ad_ineligible: !!post.ad_ineligible
    });
    setShowCreateDialog(true);
  };

  const handleUpdatePost = async () => {
    if (!newPost.content.trim()) {
      toast.error('Content is required');
      return;
    }
    setCreating(true);
    try {
      const postData = {
        content: newPost.content.trim(),
        image_url: newPost.image_url || null,
        topic_id: newPost.topic_id || null,
        is_filtered: newPost.is_filtered,
        is_archived: newPost.is_archived,
        premium_role: newPost.premium_role || null,
        ad_ineligible: !!newPost.ad_ineligible
      };
      await axios.put(
        `${API_URL}/api/posts/${editingPost.post_id}`,
        postData,
      );
      toast.success('Post updated successfully!');
      setShowCreateDialog(false);
      setEditingPost(null);
      resetPostForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    } finally {
      setCreating(false);
    }
  };

  const handleDialogClose = (open) => {
    if (!open) {
      setEditingPost(null);
      resetPostForm();
    }
    setShowCreateDialog(open);
  };

  const handleCreateTopicInline = async () => {
    if (!newTopicName.trim()) return;
    setCreatingTopic(true);
    try {
      const topicLabel = newTopicName.trim();
      const topicId = await createInlineTopic(topicLabel);
      setNewPost(p => ({ ...p, topic_id: topicId }));
      toast.success(`Topic "${topicLabel}" created!`);
      fetchTopics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create topic');
    } finally {
      setCreatingTopic(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/posts/${postId}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleToggleAfterHours = async (post) => {
    const isAfterHours = post.premium_role === 'After Hours';
    const newPremiumRole = isAfterHours ? '' : 'After Hours';
    try {
      await axios.put(`${API_URL}/api/posts/${post.post_id}`, {
        ...post,
        premium_role: newPremiumRole
      });
      toast.success(isAfterHours ? 'Post unmarked as After Hours' : 'Post marked as After Hours');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-posts">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-serif text-2xl font-bold">Posts</h2>
          <p className="text-muted-foreground">Create and manage blog posts</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-post-btn">
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
                  <DialogDescription>
                    {editingPost
                      ? 'Edit the post content. The auto-generated tag and feed position will be preserved.'
                      : 'Write the post normally. The backend will auto-generate the correct tag for the main board, Poopy Butthole, or the Archives.'}
                  </DialogDescription>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="-mt-1 -mr-1 h-8 w-8"
                  onClick={() => handleDialogClose(false)}
                  aria-label="Close post composer"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="topic">Topic</Label>
                {showNewTopicInput ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newTopicName}
                      onChange={(e) => setNewTopicName(e.target.value)}
                      placeholder="New topic name..."
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateTopicInline()}
                      data-testid="inline-topic-name-input"
                      autoFocus
                    />
                    <Button size="sm" onClick={handleCreateTopicInline} disabled={creatingTopic || !newTopicName.trim()}>
                      {creatingTopic ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setShowNewTopicInput(false); setNewTopicName(''); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Select 
                    value={newPost.topic_id || "none"} 
                    onValueChange={(value) => {
                      if (value === "create-new") {
                        setShowNewTopicInput(true);
                      } else {
                        setNewPost(p => ({ ...p, topic_id: value === "none" ? "" : value }));
                      }
                    }}
                  >
                    <SelectTrigger data-testid="post-topic-select">
                      <SelectValue placeholder="Select a topic (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No topic</SelectItem>
                      <SelectItem value="create-new" className="text-forest-700 dark:text-forest-400 font-medium">
                        + Create New Topic
                      </SelectItem>
                      {topics.map(topic => (
                        <SelectItem key={topic.topic_id} value={topic.topic_id}>
                          {topic.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  value={newPost.content}
                  onChange={(e) => setNewPost(p => ({ ...p, content: e.target.value }))}
                  placeholder={"Write the confession or story here...\nYou can reference other posts using their tags (for example #C1727 or #Poopy12)."}
                  className="min-h-[180px] font-mono text-sm"
                  data-testid="post-content-input"
                />
                <p className="text-xs text-muted-foreground">
                  Tags are generated automatically when the post is created. You can still reference existing tags anywhere in the text.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="image_url">Image URL (optional)</Label>
                <Input
                  id="image_url"
                  value={newPost.image_url}
                  onChange={(e) => setNewPost(p => ({ ...p, image_url: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  data-testid="post-image-input"
                />
                {newPost.image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-border bg-muted/30 p-2">
                    <img
                      src={newPost.image_url}
                      alt="Preview"
                      className="max-h-48 max-w-full object-contain rounded mx-auto"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      onLoad={(e) => { e.target.style.display = 'block'; }}
                      data-testid="post-image-preview"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <Checkbox
                  id="is_filtered"
                  checked={newPost.is_filtered}
                  onCheckedChange={(checked) => setNewPost(p => ({ ...p, is_filtered: checked, is_archived: checked ? false : p.is_archived }))}
                />
                <Label htmlFor="is_filtered" className="text-sm">
                  Post to Poopy Butthole section
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-4 bg-sky-50 dark:bg-sky-900/20 rounded-lg">
                <Checkbox
                  id="is_archived"
                  checked={newPost.is_archived}
                  onCheckedChange={(checked) => setNewPost(p => ({ ...p, is_archived: checked, is_filtered: checked ? false : p.is_filtered }))}
                />
                <Label htmlFor="is_archived" className="text-sm">
                  Post to Archives
                </Label>
              </div>
              <div className="space-y-2">
                <Label>Premium Only (optional)</Label>
                <Select 
                  value={newPost.premium_role || "none"} 
                  onValueChange={(value) => setNewPost(p => ({ ...p, premium_role: value === "none" ? "" : value }))}
                >
                  <SelectTrigger data-testid="post-premium-role-select">
                    <SelectValue placeholder="Visible to all users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Visible to all users</SelectItem>
                    {roles.filter(r => r.is_premium).sort((a, b) => (a.tier_level || 0) - (b.tier_level || 0)).map(role => (
                      <SelectItem key={role.role_id} value={role.name}>
                        {role.name} and above {role.monthly_price ? `($${role.monthly_price}/mo)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  If set, only users with this premium tier or higher can view the full post.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleDialogClose(false)}>
                Cancel
              </Button>
              <Button onClick={editingPost ? handleUpdatePost : handleCreatePost} disabled={creating} data-testid="submit-post-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingPost ? 'Update Post' : 'Create Post')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        <Button
          variant={filterType === 'main' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterType('main')}
        >
          Main Feed
        </Button>
        <Button
          variant={filterType === 'filtered' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterType('filtered')}
        >
          Poopy Butthole
        </Button>
        <Button
          variant={filterType === 'archives' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterType('archives')}
        >
          Archives
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
        </div>
      ) : posts.length === 0 ? (
        <Card className="text-center py-10">
          <CardContent>
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">
              {filterType === 'filtered'
                ? 'No posts in Poopy Butthole section'
                : filterType === 'archives'
                  ? 'No posts in Archives yet'
                  : 'No posts yet. Create your first post!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <Card key={post.post_id} className="card-hover">
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!post.is_filtered && (
                        <span className="font-mono text-xs bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 px-2 py-0.5 rounded">
                          {post.tag}
                        </span>
                      )}
                      {post.topic_name && (
                        <Badge variant="outline">{post.topic_name}</Badge>
                      )}
                      {post.is_filtered && (
                        <Badge variant="secondary" className="text-yellow-700 bg-yellow-100">Filtered</Badge>
                      )}
                      {post.is_archived && (
                        <Badge variant="secondary" className="text-sky-700 bg-sky-100">Archives</Badge>
                      )}
                      {post.premium_role && (
                        <Badge className="bg-purple-600 text-white text-xs">Premium: {post.premium_role}</Badge>
                      )}
                    </div>
                    <Link 
                      to={`/post/${post.post_id}`}
                      className="font-serif text-lg font-semibold hover:text-forest-700 dark:hover:text-forest-400"
                    >
                      {post.is_filtered ? (post.content?.slice(0, 60) || 'Filtered Post') : post.tag}
                    </Link>
                    <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                      {post.content}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>{post.author_username}</span>
                      <span>{post.comment_count} comments</span>
                      <span>{format(new Date(post.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`${post.premium_role === 'After Hours' ? 'text-violet-500 bg-violet-100 dark:bg-violet-900/30' : 'text-muted-foreground hover:text-foreground'} hover:bg-muted`}
                      onClick={() => handleToggleAfterHours(post)}
                      title={post.premium_role === 'After Hours' ? 'Remove After Hours' : 'Mark as After Hours'}
                      data-testid={`toggle-after-hours-${post.post_id}`}
                    >
                      <Moon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-foreground hover:bg-muted"
                      onClick={() => handleEditPost(post)}
                      data-testid={`edit-post-${post.post_id}`}
                    >
                      <PenSquare className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeletePost(post.post_id)}
                      data-testid={`delete-post-${post.post_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Users Management
const UsersTab = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteContent, setDeleteContent] = useState(false);
  const [filterVerified, setFilterVerified] = useState('all');
  const [showSecBanDialog, setShowSecBanDialog] = useState(false);
  const [secBanTarget, setSecBanTarget] = useState(null);
  const [secBanReason, setSecBanReason] = useState('');
  const [showInvalidateNameDialog, setShowInvalidateNameDialog] = useState(false);
  const [invalidateNameTarget, setInvalidateNameTarget] = useState(null);
  const [invalidateNameReason, setInvalidateNameReason] = useState('');
  const [allBadges, setAllBadges] = useState([]);
  const [showBadgeDialog, setShowBadgeDialog] = useState(false);
  const [badgeTargetUser, setBadgeTargetUser] = useState(null);

  const getUserStatus = (user) => {
    if (!user.email_verified) return 'pending';
    if (user.is_on_probation) return 'probation';
    return 'verified';
  };

  const formatLastActive = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const fetchUsers = async () => {
    try {
      const [usersRes, rolesRes, badgesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users?limit=100`),
        axios.get(`${API_URL}/api/admin/roles`),
        axios.get(`${API_URL}/api/admin/badges`)
      ]);
      setUsers(usersRes.data.users);
      setRoles(rolesRes.data.roles);
      setAllBadges(badgesRes.data.badges || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        
      );
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleSendVerification = async (userId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/users/${userId}/send-verification`,
        
      );
      if (response.data?.email_sent === false) {
        toast.error(response.data?.message || 'Email service unavailable');
        return;
      }
      toast.success(response.data?.message || 'Verification email sent. The user needs to reply to it to verify their account.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification');
    }
  };

  const handleSendBulkVerification = async () => {
    const unverifiedSelected = selectedUsers.filter(id => {
      const user = users.find(u => u.user_id === id);
      return user && !user.email_verified && !user.oauth_provider;
    });
    
    if (unverifiedSelected.length === 0) {
      toast.error('No unverified users selected');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/users/send-verification-bulk`,
        { user_ids: unverifiedSelected },
        
      );
      const successful = response.data.results.filter(r => r.status === 'success').length;
      toast.success(`Sent ${successful} verification email(s)`);
      setSelectedUsers([]);
    } catch (error) {
      toast.error('Failed to send verification emails');
    }
  };

  const handleSecurityBan = async () => {
    if (!secBanTarget) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${secBanTarget.user_id}/security-ban`,
        { reason: secBanReason || null },
        
      );
      toast.success(`Security ban applied to ${secBanTarget.display_name || secBanTarget.username}`);
      setShowSecBanDialog(false);
      setSecBanTarget(null);
      setSecBanReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply security ban');
    }
  };

  const handleSecurityUnban = async (userId) => {
    if (!window.confirm('Remove security ban? This will unblock the user and remove their IP bans.')) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/security-unban`,
        
      );
      toast.success('Security ban removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove security ban');
    }
  };

  const handleInvalidateDisplayName = async () => {
    if (!invalidateNameTarget) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${invalidateNameTarget.user_id}/invalidate-display-name`,
        { reason: invalidateNameReason || null },
        
      );
      toast.success('Username invalidated. The user must choose a new one.');
      setShowInvalidateNameDialog(false);
      setInvalidateNameTarget(null);
      setInvalidateNameReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invalidate username');
    }
  };

  const handleProbationStatusChange = async (targetUser, shouldBeOnProbation) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/users/${targetUser.user_id}/probation`,
        { is_on_probation: shouldBeOnProbation },
      );
      toast.success(response.data?.message || (shouldBeOnProbation ? 'User placed on probation.' : 'User pre-approved successfully.'));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update probation status');
    }
  };


  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${userToDelete.user_id}?delete_content=${deleteContent}`,
        
      );
      toast.success(`User deleted. ${deleteContent ? 'Content removed.' : 'Content preserved.'}`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      setDeleteContent(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const [showBulkMuteDialog, setShowBulkMuteDialog] = useState(false);
  const [bulkMuteDays, setBulkMuteDays] = useState('1');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUsers = users.filter(user => {
    const status = getUserStatus(user);
    if (filterVerified !== 'all' && status !== filterVerified) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = (user.display_name || '').toLowerCase().includes(q);
      const matchUsername = (user.username || '').toLowerCase().includes(q);
      const matchReal = (user.real_name || '').toLowerCase().includes(q);
      const matchEmail = (user.email || '').toLowerCase().includes(q);
      if (!matchName && !matchUsername && !matchReal && !matchEmail) return false;
    }
    return true;
  });

  const pendingCount = users.filter(u => getUserStatus(u) === 'pending').length;
  const probationCount = users.filter(u => getUserStatus(u) === 'probation').length;
  const verifiedCount = users.filter(u => getUserStatus(u) === 'verified').length;

  // Compute bulk action context
  const selectedData = users.filter(u => selectedUsers.includes(u.user_id));
  const unverifiedSelected = selectedData.filter(u => getUserStatus(u) === 'pending');
  const bannedSelected = selectedData.filter(u => u.is_banned);
  const unbannedSelected = selectedData.filter(u => !u.is_banned);
  const mutedSelected = selectedData.filter(u => u.muted_until && new Date(u.muted_until) > new Date());
  const unmutedSelected = selectedData.filter(u => !u.muted_until || new Date(u.muted_until) <= new Date());

  const handleBulkAction = async (action, extraData = {}) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/users/${action}`,
        { user_ids: selectedUsers, ...extraData },
        
      );
      toast.success(res.data.message);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleToggleBadge = async (userId, badgeId, hasIt) => {
    try {
      if (hasIt) {
        await axios.delete(`${API_URL}/api/admin/users/${userId}/badges/${badgeId}`);
        toast.success('Badge removed');
      } else {
        await axios.post(`${API_URL}/api/admin/users/${userId}/badges/${badgeId}`);
        toast.success('Badge assigned');
      }
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update badge');
    }
  };

  const openBadgeDialog = (user) => {
    setBadgeTargetUser(user);
    setShowBadgeDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="font-serif text-2xl font-bold">Users</h2>
          <p className="text-muted-foreground">Manage user accounts and roles</p>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.length > 0 && (
        <Card className="border-forest-300 dark:border-forest-700">
          <CardContent className="py-3 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium mr-2">{selectedUsers.length} selected:</span>
            {unverifiedSelected.length > 0 && (
              <Button size="sm" variant="outline" onClick={handleSendBulkVerification} data-testid="bulk-send-verify">
                <Mail className="w-3 h-3 mr-1" /> Verify ({unverifiedSelected.length})
              </Button>
            )}
            {unbannedSelected.length > 0 && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleBulkAction('bulk-ban')} data-testid="bulk-ban">
                <Ban className="w-3 h-3 mr-1" /> Ban ({unbannedSelected.length})
              </Button>
            )}
            {bannedSelected.length > 0 && (
              <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleBulkAction('bulk-unban')} data-testid="bulk-unban">
                Unban ({bannedSelected.length})
              </Button>
            )}
            {unmutedSelected.length > 0 && (
              <Button size="sm" variant="outline" className="text-amber-600 border-amber-300" onClick={() => setShowBulkMuteDialog(true)} data-testid="bulk-mute">
                <VolumeX className="w-3 h-3 mr-1" /> Mute ({unmutedSelected.length})
              </Button>
            )}
            {mutedSelected.length > 0 && (
              <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleBulkAction('bulk-unmute')} data-testid="bulk-unmute">
                Unmute ({mutedSelected.length})
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('bulk-clear-blocks')} data-testid="bulk-clear-blocks">
              Clear Blocks
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => { if (window.confirm(`Delete ${selectedUsers.length} user(s)? This cannot be undone.`)) handleBulkAction('bulk-delete'); }} data-testid="bulk-delete">
              <Trash2 className="w-3 h-3 mr-1" /> Delete ({selectedUsers.length})
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Bulk Mute Dialog */}
      <Dialog open={showBulkMuteDialog} onOpenChange={setShowBulkMuteDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Mute {unmutedSelected.length} user(s)</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Duration</Label>
            <Select value={bulkMuteDays} onValueChange={setBulkMuteDays}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1,3,7,14,28].map(d => <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkMuteDialog(false)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { handleBulkAction('bulk-mute', { days: parseInt(bulkMuteDays) }); setShowBulkMuteDialog(false); }}>
              Mute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filter Tabs */}
      <div className="flex gap-2 items-center">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Button
          variant={filterVerified === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterVerified('all')}
        >
          All ({users.length})
        </Button>
        <Button
          variant={filterVerified === 'verified' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterVerified('verified')}
        >
          Verified ({verifiedCount})
        </Button>
        <Button
          variant={filterVerified === 'probation' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterVerified('probation')}
          className={probationCount > 0 ? 'border-amber-500' : ''}
        >
          Probation ({probationCount})
        </Button>
        <Button
          variant={filterVerified === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterVerified('pending')}
          className={pendingCount > 0 ? 'border-yellow-500' : ''}
        >
          Pending ({pendingCount})
        </Button>
      </div>

      {/* Search Bar */}
      <div className="relative" data-testid="user-search-bar">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by display name, real name, or email..."
          className="pl-9 pr-9"
          data-testid="user-search-input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="user-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox 
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedUsers(filteredUsers.map(u => u.user_id));
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                  />
                </TableHead>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(user => (
                <TableRow key={user.user_id}>
                  <TableCell>
                    <Checkbox 
                      checked={selectedUsers.includes(user.user_id)}
                      onCheckedChange={() => toggleUserSelection(user.user_id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user.profile_picture} />
                        <AvatarFallback>{(user.display_name || user.username)?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <Link to={`/profile/${user.user_id}`} className="font-medium hover:text-forest-600 hover:underline" data-testid={`user-profile-link-${user.user_id}`}>
                          {user.display_name || user.username}
                        </Link>
                        {user.oauth_provider && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({user.oauth_provider})
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Badge
                        variant={user.premium_role_name ? 'outline' : (user.role === 'admin' || user.role === 'superadmin' ? 'default' : 'secondary')}
                        className={getRoleBadgeClass(user)}
                      >
                        {getDisplayedUserRole(user)}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {getUserStatus(user) === 'verified' ? (
                        <Badge variant="outline" className="text-green-600 border-green-600">Verified</Badge>
                      ) : getUserStatus(user) === 'probation' ? (
                        <Badge variant="outline" className="text-amber-700 border-amber-600">Probation</Badge>
                      ) : (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-600">Pending</Badge>
                      )}
                      {user.oauth_provider && (
                        <Badge variant="outline" className="text-blue-600 border-blue-600">{user.oauth_provider}</Badge>
                      )}
                      {user.is_banned && (
                        <Badge className="bg-red-600 text-white text-xs"><Ban className="w-3 h-3 mr-0.5" />Banned</Badge>
                      )}
                      {user.security_banned && (
                        <Badge className="bg-red-900 text-white text-xs" data-testid={`security-ban-badge-${user.user_id}`}>
                          <ShieldAlert className="w-3 h-3 mr-0.5" />Security Ban
                        </Badge>
                      )}
                      {user.muted_until && new Date(user.muted_until) > new Date() && (
                        <Badge className="bg-amber-600 text-white text-xs"><VolumeX className="w-3 h-3 mr-0.5" />Muted</Badge>
                      )}
                      {user.premium_tier > 0 && (
                        <Badge variant="outline" className="text-purple-600 border-purple-600">
                          Member
                        </Badge>
                      )}
                      {user.must_change_display_name && (
                        <Badge variant="outline" className="text-amber-700 border-amber-500">
                          Username Reset Required
                        </Badge>
                      )}
                      {/* Show assigned custom badges */}
                      {(user.badges || []).map(bId => {
                        const b = allBadges.find(x => x.badge_id === bId);
                        return b ? (
                          <span key={bId} className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-semibold" style={{ backgroundColor: b.bg_color, color: b.text_color }}>
                            {b.text}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatLastActive(user.last_active_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Select
                        value={getDisplayedUserRole(user)}
                        onValueChange={(value) => handleRoleChange(user.user_id, value)}
                      >
                        <SelectTrigger className="w-28" data-testid={`role-select-${user.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="superadmin">Super Admin</SelectItem>
                          {roles.filter(r => !['user', 'admin', 'superadmin'].includes(r.name)).map(role => (
                            <SelectItem key={role.role_id} value={role.name}>{role.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {getUserStatus(user) === 'pending' && !user.oauth_provider && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSendVerification(user.user_id)}
                          title="Send verification email"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                      )}

                      {user.user_id !== currentUser?.user_id && user.email_verified && (
                        <Button
                          variant="outline"
                          size="sm"
                          className={user.is_on_probation ? 'text-green-700 hover:text-green-800' : 'text-amber-700 hover:text-amber-800'}
                          onClick={() => handleProbationStatusChange(user, !user.is_on_probation)}
                          title={user.is_on_probation ? 'Pre-approve user' : 'Place user on probation'}
                          data-testid={user.is_on_probation ? `pre-approve-user-${user.user_id}` : `place-on-probation-${user.user_id}`}
                        >
                          {user.is_on_probation ? <UserCheck className="w-4 h-4 mr-1" /> : <Clock className="w-4 h-4 mr-1" />}
                          {user.is_on_probation ? 'Pre-Approve' : 'Place On Probation'}
                        </Button>
                      )}

                      {user.user_id !== currentUser?.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-amber-700 hover:text-amber-800"
                          onClick={() => {
                            setInvalidateNameTarget(user);
                            setShowInvalidateNameDialog(true);
                          }}
                          title="Invalidate username"
                          data-testid={`invalidate-username-${user.user_id}`}
                        >
                          <ShieldOff className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {user.user_id !== currentUser?.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setUserToDelete(user);
                            setShowDeleteDialog(true);
                          }}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Security Ban/Unban - superadmin only */}
                      {currentUser?.role === 'superadmin' && user.user_id !== currentUser?.user_id && !['admin', 'superadmin'].includes(user.role) && (
                        user.security_banned ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => handleSecurityUnban(user.user_id)}
                            title="Remove security ban"
                            data-testid={`security-unban-${user.user_id}`}
                          >
                            <ShieldAlert className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-800 hover:text-red-900"
                            onClick={() => {
                              setSecBanTarget(user);
                              setShowSecBanDialog(true);
                            }}
                            title="Security ban (IP + cookie + account)"
                            data-testid={`security-ban-${user.user_id}`}
                          >
                            <ShieldAlert className="w-4 h-4" />
                          </Button>
                        )
                      )}
                      {/* Badge assignment button */}
                      {allBadges.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openBadgeDialog(user)}
                          title="Manage badges"
                          className="text-amber-600 hover:text-amber-700"
                          data-testid={`badge-btn-${user.user_id}`}
                        >
                          <Crown className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.display_name || userToDelete?.username}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <Checkbox
                id="deleteContent"
                checked={deleteContent}
                onCheckedChange={setDeleteContent}
              />
              <div>
                <Label htmlFor="deleteContent" className="font-medium">
                  Also delete all their comments and reactions
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  If unchecked, their comments will remain but show "(account deleted)" next to their name.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Security Ban Dialog */}
      <Dialog open={showSecBanDialog} onOpenChange={setShowSecBanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="w-5 h-5" />
              Security Ban — {secBanTarget?.display_name || secBanTarget?.username}
            </DialogTitle>
            <DialogDescription>
              This will permanently ban the user's account, block their IP address(es), and set a persistent cookie on their device. 
              This is a severe action reserved for security violations. Only you (superadmin) can reverse it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <Input 
              value={secBanReason} 
              onChange={(e) => setSecBanReason(e.target.value)} 
              placeholder="Security violation reason..." 
              data-testid="security-ban-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSecBanDialog(false); setSecBanTarget(null); setSecBanReason(''); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleSecurityBan}
              data-testid="confirm-security-ban-btn"
            >
              <ShieldAlert className="w-4 h-4 mr-1" /> Apply Security Ban
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInvalidateNameDialog} onOpenChange={setShowInvalidateNameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ShieldOff className="w-5 h-5" />
              Invalidate Username — {invalidateNameTarget?.display_name || invalidateNameTarget?.username}
            </DialogTitle>
            <DialogDescription>
              This will immediately replace the user's visible name with a temporary `User#####` placeholder, add their current username to the banned words list, and require them to choose a new username before continuing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <Input
              value={invalidateNameReason}
              onChange={(e) => setInvalidateNameReason(e.target.value)}
              placeholder="Optional moderation reason..."
              data-testid="invalidate-username-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowInvalidateNameDialog(false);
                setInvalidateNameTarget(null);
                setInvalidateNameReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleInvalidateDisplayName}
              data-testid="confirm-invalidate-username-btn"
            >
              <ShieldOff className="w-4 h-4 mr-1" /> Invalidate Username
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Badge Assignment Dialog */}
      <Dialog open={showBadgeDialog} onOpenChange={setShowBadgeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Manage Badges — {badgeTargetUser?.display_name || badgeTargetUser?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {allBadges.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No badges created yet. Create badges in the Badges tab first.</p>
            ) : (
              allBadges.map(badge => {
                const userBadges = badgeTargetUser?.badges || [];
                const hasBadge = userBadges.includes(badge.badge_id);
                return (
                  <div key={badge.badge_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold"
                      style={{ backgroundColor: badge.bg_color, color: badge.text_color }}
                    >
                      {badge.text}
                    </span>
                    <Button
                      size="sm"
                      variant={hasBadge ? 'default' : 'outline'}
                      onClick={() => handleToggleBadge(badgeTargetUser.user_id, badge.badge_id, hasBadge)}
                      data-testid={`toggle-badge-${badge.badge_id}`}
                    >
                      {hasBadge ? <><Check className="w-3 h-3 mr-1" /> Assigned</> : 'Assign'}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};
const RolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', permissions: [], allowed_emojis: [], allowed_themes: [], emoji_input: '' });
  const [saving, setSaving] = useState(false);
  const [allThemes, setAllThemes] = useState([]);

  const allPermissions = [
    'read', 'comment', 'create_post', 'edit_post', 'delete_post', 
    'manage_users', 'manage_roles', 'view_edit_history', 'hide_comments', 
    'view_anonymous_identity', 'assign_tags', 'moderate_comments', 'report_comments',
    'view_real_names', 'view_private_photos', 'view_bios',
    'admin_tab_overview', 'admin_tab_posts', 'admin_tab_topics', 'admin_tab_users',
    'admin_tab_roles', 'admin_tab_badges', 'admin_tab_themes', 'admin_tab_settings', 'admin_tab_approvals', 'admin_tab_reports', 'admin_tab_security'
  ];

  const premiumRewards = [
    'no_ads', 'custom_banner', 'after_hours_access', 'custom_themes', 'top_billing', 'view_archives'
  ];

  const fetchRoles = async () => {
    try {
      const [rolesRes, themesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/roles`),
        axios.get(`${API_URL}/api/admin/themes`)
      ]);
      setRoles(rolesRes.data.roles);
      setAllThemes(themesRes.data.themes || []);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const openCreateDialog = () => {
    setEditingRole(null);
    setRoleForm({ name: '', permissions: [], allowed_emojis: ['👍', '❤️', '😂', '😮', '😢', '😡'], allowed_themes: [], emoji_input: '' });
    setShowDialog(true);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      permissions: [...(role.permissions || [])],
      allowed_emojis: [...(role.allowed_emojis || [])],
      allowed_themes: [...(role.allowed_themes || [])],
      emoji_input: ''
    });
    setShowDialog(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name) {
      toast.error('Role name is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: roleForm.name,
        permissions: roleForm.permissions,
        allowed_emojis: roleForm.allowed_emojis,
        allowed_themes: roleForm.allowed_themes
      };

      if (editingRole) {
        await axios.put(
          `${API_URL}/api/admin/roles/${editingRole.role_id}`,
          payload,
          
        );
        toast.success('Role updated');
      } else {
        await axios.post(
          `${API_URL}/api/admin/roles`,
          payload,
          
        );
        toast.success('Role created');
      }
      setShowDialog(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/roles/${roleId}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePermission = (perm) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const addEmoji = () => {
    const emoji = roleForm.emoji_input.trim();
    if (emoji && !roleForm.allowed_emojis.includes(emoji)) {
      setRoleForm(prev => ({
        ...prev,
        allowed_emojis: [...prev.allowed_emojis, emoji],
        emoji_input: ''
      }));
    } else {
      setRoleForm(prev => ({ ...prev, emoji_input: '' }));
    }
  };

  const removeEmoji = (emoji) => {
    setRoleForm(prev => ({
      ...prev,
      allowed_emojis: prev.allowed_emojis.filter(e => e !== emoji)
    }));
  };

  const [dragIndex, setDragIndex] = useState(null);

  const handleEmojiDragStart = (index) => {
    setDragIndex(index);
  };

  const handleEmojiDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const updated = [...roleForm.allowed_emojis];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(index, 0, moved);
    setRoleForm(prev => ({ ...prev, allowed_emojis: updated }));
    setDragIndex(index);
  };

  const handleEmojiDragEnd = () => {
    setDragIndex(null);
  };

  const isBuiltIn = (name) => ['user', 'admin', 'superadmin'].includes(name);
  const isProtected = (role) => isBuiltIn(role.name) || role.is_premium;

  return (
    <div className="space-y-6" data-testid="admin-roles">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-serif text-2xl font-bold">Roles</h2>
          <p className="text-muted-foreground">Define user roles, permissions, and emoji access</p>
        </div>
        <Button className="btn-primary" onClick={openCreateDialog} data-testid="create-role-btn">
          <Plus className="w-4 h-4 mr-2" /> New Role
        </Button>
      </div>

      {/* Role Dialog (Create/Edit) */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
            <DialogDescription>
              {editingRole ? 'Update permissions and emoji access for this role' : 'Define a custom role with specific permissions and emoji access'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="roleName">Role Name</Label>
              <Input
                id="roleName"
                value={roleForm.name}
                onChange={(e) => setRoleForm(p => ({ ...p, name: e.target.value }))}
                placeholder="moderator"
                disabled={editingRole && isBuiltIn(editingRole.name)}
                data-testid="role-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="flex flex-wrap gap-2">
                {allPermissions.map(perm => (
                  <Button
                    key={perm}
                    variant={roleForm.permissions.includes(perm) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => togglePermission(perm)}
                    className="text-xs"
                  >
                    {perm.replace(/_/g, ' ')}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span>Premium Rewards</span>
                <Badge className="bg-amber-500/10 text-amber-600 border border-amber-400 text-[10px]">Gold Perks</Badge>
              </Label>
              <div className="flex flex-wrap gap-2">
                {premiumRewards.map(perk => (
                  <Button
                    key={perk}
                    variant={roleForm.permissions.includes(perk) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => togglePermission(perk)}
                    className={`text-xs ${
                      roleForm.permissions.includes(perk)
                        ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500'
                        : 'border-amber-400 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30'
                    }`}
                    data-testid={`perk-toggle-${perk}`}
                  >
                    {perk.replace(/_/g, ' ')}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Gold perks grant premium features like ad removal, custom banners, exclusive content access, and themes.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Allowed Emojis</Label>
              <div className="flex flex-wrap gap-1 min-h-[40px] p-2 border border-border rounded-lg bg-muted/30">
                {roleForm.allowed_emojis.map((emoji, index) => (
                  <button
                    key={`${emoji}-${index}`}
                    draggable
                    onDragStart={() => handleEmojiDragStart(index)}
                    onDragOver={(e) => handleEmojiDragOver(e, index)}
                    onDragEnd={handleEmojiDragEnd}
                    className={`text-xl p-1 rounded hover:bg-destructive/10 transition-colors group relative cursor-grab active:cursor-grabbing ${dragIndex === index ? 'opacity-50 scale-90' : ''}`}
                    onClick={() => removeEmoji(emoji)}
                    title={`Drag to reorder, click to remove ${emoji}`}
                  >
                    {emoji}
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-destructive rounded-full text-[8px] text-white flex items-center justify-center opacity-0 group-hover:opacity-100">x</span>
                  </button>
                ))}
                {roleForm.allowed_emojis.length === 0 && (
                  <span className="text-xs text-muted-foreground">No emojis added</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={roleForm.emoji_input}
                  onChange={(e) => setRoleForm(p => ({ ...p, emoji_input: e.target.value }))}
                  placeholder="Type or paste any emoji..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmoji())}
                  data-testid="emoji-input"
                />
                <Button size="sm" variant="outline" onClick={addEmoji} data-testid="add-emoji-btn">Add</Button>
              </div>
              <p className="text-xs text-muted-foreground">Type or paste any emoji and press Enter or Add. Click an emoji to remove it.</p>
            </div>

            {/* Allowed Themes */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span>Allowed Themes</span>
                <Badge className="bg-purple-500/10 text-purple-600 border border-purple-400 text-[10px]">Theme Access</Badge>
              </Label>
              <div className="flex flex-wrap gap-2">
                {allThemes.filter(t => t.theme_id !== 'theme_default').map(theme => (
                  <Button
                    key={theme.theme_id}
                    variant={roleForm.allowed_themes.includes(theme.theme_id) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setRoleForm(prev => ({
                      ...prev,
                      allowed_themes: prev.allowed_themes.includes(theme.theme_id)
                        ? prev.allowed_themes.filter(t => t !== theme.theme_id)
                        : [...prev.allowed_themes, theme.theme_id]
                    }))}
                    className={`text-xs ${
                      roleForm.allowed_themes.includes(theme.theme_id)
                        ? 'bg-purple-500 hover:bg-purple-600 text-white border-purple-500'
                        : 'border-purple-400 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950/30'
                    }`}
                    data-testid={`theme-toggle-${theme.theme_id}`}
                  >
                    <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: theme.dark_colors?.primary || '#666' }} />
                    {theme.name}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Select which themes users with this role can access. Default theme is always available.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRole} disabled={saving} data-testid="submit-role-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingRole ? 'Save Changes' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
        </div>
      ) : (
        <div className="space-y-4">
          {roles.map(role => (
            <Card key={role.role_id} data-testid={`role-card-${role.name}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg capitalize">{role.name}</h3>
                      {isBuiltIn(role.name) && (
                        <Badge variant="outline" className="text-xs">Built-in</Badge>
                      )}
                      {role.is_premium && (
                        <Badge className="bg-gradient-to-r from-amber-500 to-rose-500 text-white text-xs">Premium Tier {role.tier_level}</Badge>
                      )}
                      {role.monthly_price > 0 && (
                        <Badge variant="secondary" className="text-xs">${role.monthly_price}/mo</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {role.permissions?.filter(p => !['no_ads', 'custom_banner', 'after_hours_access', 'custom_themes', 'top_billing'].includes(p)).map(perm => (
                        <Badge key={perm} variant="secondary" className="text-xs">{perm.replace(/_/g, ' ')}</Badge>
                      ))}
                      {role.permissions?.filter(p => ['no_ads', 'custom_banner', 'after_hours_access', 'custom_themes', 'top_billing'].includes(p)).map(perk => (
                        <Badge key={perk} className="text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-400">{perk.replace(/_/g, ' ')}</Badge>
                      ))}
                    </div>
                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground mb-1">Allowed Emojis:</p>
                      <div className="flex flex-wrap gap-1">
                        {role.allowed_emojis?.map(emoji => (
                          <span key={emoji} className="text-lg">{emoji}</span>
                        ))}
                      </div>
                    </div>
                    {role.allowed_themes?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground mb-1">Allowed Themes:</p>
                        <div className="flex flex-wrap gap-1">
                          {role.allowed_themes.map(tid => {
                            const t = allThemes.find(th => th.theme_id === tid);
                            return t ? (
                              <Badge key={tid} variant="outline" className="text-xs border-purple-400 text-purple-600 dark:text-purple-400">
                                {t.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(role)} data-testid={`edit-role-${role.name}`}>
                      <PenSquare className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    {!isProtected(role) && (
                      <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => handleDeleteRole(role.role_id)} data-testid={`delete-role-${role.name}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Badges Management
const BADGE_ICONS = [
  'star', 'crown', 'shield', 'sparkles', 'heart', 'flame', 'zap', 'gem', 
  'award', 'trophy', 'medal', 'flag', 'moon', 'sun', 'bolt', 'music',
  'eye', 'lock', 'key', 'skull', 'ghost', 'rocket', 'leaf', 'snowflake'
];

const BadgesTab = () => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [form, setForm] = useState({ text: '', bg_color: '#6366f1', text_color: '#FFFFFF', icon: 'star' });
  const [saving, setSaving] = useState(false);

  const fetchBadges = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/badges`);
      setBadges(res.data.badges || []);
    } catch { toast.error('Failed to load badges'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBadges(); }, []);

  const openCreate = () => {
    setEditingBadge(null);
    setForm({ text: '', bg_color: '#6366f1', text_color: '#FFFFFF', icon: 'star' });
    setShowDialog(true);
  };

  const openEdit = (badge) => {
    setEditingBadge(badge);
    setForm({ text: badge.text, bg_color: badge.bg_color, text_color: badge.text_color, icon: badge.icon });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.text.trim()) { toast.error('Badge text required'); return; }
    setSaving(true);
    try {
      if (editingBadge) {
        await axios.put(`${API_URL}/api/admin/badges/${editingBadge.badge_id}`, form);
        toast.success('Badge updated');
      } else {
        await axios.post(`${API_URL}/api/admin/badges`, form);
        toast.success('Badge created');
      }
      setShowDialog(false);
      fetchBadges();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save badge'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (badgeId) => {
    if (!window.confirm('Delete this badge? It will be removed from all users.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/badges/${badgeId}`);
      toast.success('Badge deleted');
      fetchBadges();
    } catch { toast.error('Failed to delete badge'); }
  };

  return (
    <div className="space-y-6" data-testid="badges-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-serif font-bold">Custom Badges</h2>
        <Button onClick={openCreate} data-testid="create-badge-btn"><Plus className="w-4 h-4 mr-1" /> Create Badge</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : badges.length === 0 ? (
        <Card className="text-center py-10"><CardContent><p className="text-muted-foreground">No custom badges yet. Create one to get started.</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {badges.map(badge => (
            <Card key={badge.badge_id} data-testid={`badge-card-${badge.badge_id}`}>
              <CardContent className="pt-4 pb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold"
                    style={{ backgroundColor: badge.bg_color, color: badge.text_color }}
                    data-testid={`badge-preview-${badge.badge_id}`}
                  >
                    {badge.text}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(badge)} data-testid={`edit-badge-${badge.badge_id}`}>
                    <PenSquare className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(badge.badge_id)} data-testid={`delete-badge-${badge.badge_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBadge ? 'Edit Badge' : 'Create Badge'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Preview */}
            <div className="text-center py-4">
              <span
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm"
                style={{ backgroundColor: form.bg_color, color: form.text_color }}
                data-testid="badge-live-preview"
              >
                {form.text || 'Preview'}
              </span>
            </div>

            {/* Text */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Badge Text</label>
              <input
                type="text"
                value={form.text}
                onChange={(e) => setForm(f => ({ ...f, text: e.target.value }))}
                maxLength={20}
                className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
                placeholder="e.g. VIP, OG, Staff"
                data-testid="badge-text-input"
              />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">Background Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.bg_color}
                    onChange={(e) => setForm(f => ({ ...f, bg_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                    data-testid="badge-bg-color-picker"
                  />
                  <input
                    type="text"
                    value={form.bg_color}
                    onChange={(e) => setForm(f => ({ ...f, bg_color: e.target.value }))}
                    className="flex-1 px-2 py-1 border rounded text-sm font-mono bg-background text-foreground"
                    data-testid="badge-bg-color-hex"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Text Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.text_color}
                    onChange={(e) => setForm(f => ({ ...f, text_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                    data-testid="badge-text-color-picker"
                  />
                  <input
                    type="text"
                    value={form.text_color}
                    onChange={(e) => setForm(f => ({ ...f, text_color: e.target.value }))}
                    className="flex-1 px-2 py-1 border rounded text-sm font-mono bg-background text-foreground"
                    data-testid="badge-text-color-hex"
                  />
                </div>
              </div>
            </div>

            {/* Icon selector */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Icon</label>
              <div className="flex flex-wrap gap-2">
                {BADGE_ICONS.map(iconName => (
                  <button
                    key={iconName}
                    onClick={() => setForm(f => ({ ...f, icon: iconName }))}
                    className={`px-2 py-1 rounded text-xs border transition-all ${
                      form.icon === iconName ? 'border-primary bg-primary/10 font-semibold' : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`icon-option-${iconName}`}
                  >
                    {iconName}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-badge-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editingBadge ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Topics Management
const TopicsTab = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTopic, setNewTopic] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/topics`);
      setTopics(response.data.topics);
    } catch (error) {
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  const handleCreateTopic = async () => {
    if (!newTopic.name) {
      toast.error('Topic name is required');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(
        `${API_URL}/api/topics`,
        newTopic,
        
      );
      toast.success('Topic created!');
      setShowCreateDialog(false);
      setNewTopic({ name: '', description: '' });
      fetchTopics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create topic');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Delete this topic? Posts using it will be unassigned.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/topics/${topicId}`);
      toast.success('Topic deleted');
      fetchTopics();
    } catch (error) {
      toast.error('Failed to delete topic');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-topics">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-serif text-2xl font-bold">Topics</h2>
          <p className="text-muted-foreground">Manage post categories</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-topic-btn">
              <Plus className="w-4 h-4 mr-2" /> New Topic
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Topic</DialogTitle>
              <DialogDescription>Add a new category for organizing posts</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="topicName">Name</Label>
                <Input
                  id="topicName"
                  value={newTopic.name}
                  onChange={(e) => setNewTopic(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Relationships, Work, Family"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="topicDesc">Description (optional)</Label>
                <Textarea
                  id="topicDesc"
                  value={newTopic.description}
                  onChange={(e) => setNewTopic(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of this topic..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateTopic} disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
        </div>
      ) : topics.length === 0 ? (
        <Card className="text-center py-10">
          <CardContent>
            <Tag className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No topics yet. Create your first topic!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {topics.map(topic => (
            <Card key={topic.topic_id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold">{topic.name}</h3>
                    {topic.description && (
                      <p className="text-sm text-muted-foreground mt-1">{topic.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      {topic.post_count || 0} posts
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDeleteTopic(topic.topic_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Site Settings
const SiteSettingsTab = () => {
  const parseBannedDisplayNameWords = (rawValue) => rawValue
    .split(/\r?\n|,/)
    .map((value) => value.trim())
    .filter(Boolean);

  const [settings, setSettings] = useState({
    google_form_url: '',
    about_text: '',
    how_it_works_text: '',
    faq_text: '',
    moderation_policy_text: '',
    privacy_text: '',
    terms_text: '',
    contact_text: '',
    banned_display_name_words: [],
    ad_frequency_guest: 1,
    ad_frequency_member: 3,
    api_post_user_id: '',
    submission_mode: 'google_form',
    confession_form_intro: '',
    confession_image_help_text: '',
    confession_submit_button_text: 'Submit Confession'
  });
  const [rules, setRules] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [savingFaq, setSavingFaq] = useState(false);
  const [bannedDisplayNameWordsText, setBannedDisplayNameWordsText] = useState('');
  const [faqItems, setFaqItems] = useState([]);
  const [newRuleTitle, setNewRuleTitle] = useState('');
  const [newRuleText, setNewRuleText] = useState('');
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingRuleTitle, setEditingRuleTitle] = useState('');
  const [editingRuleText, setEditingRuleText] = useState('');
  const [newFaqQuestion, setNewFaqQuestion] = useState('');
  const [newFaqAnswer, setNewFaqAnswer] = useState('');
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [editingFaqQuestion, setEditingFaqQuestion] = useState('');
  const [editingFaqAnswer, setEditingFaqAnswer] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [settingsRes, usersRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/settings`),
          axios.get(`${API_URL}/api/admin/users`)
        ]);
        setSettings({
          google_form_url: settingsRes.data.google_form_url || '',
          about_text: settingsRes.data.about_text || '',
          how_it_works_text: settingsRes.data.how_it_works_text || '',
          faq_text: settingsRes.data.faq_text || '',
          moderation_policy_text: settingsRes.data.moderation_policy_text || '',
          privacy_text: settingsRes.data.privacy_text || '',
          terms_text: settingsRes.data.terms_text || '',
          contact_text: settingsRes.data.contact_text || '',
          banned_display_name_words: settingsRes.data.banned_display_name_words || [],
          ad_frequency_guest: settingsRes.data.ad_frequency_guest ?? 1,
          ad_frequency_member: settingsRes.data.ad_frequency_member ?? 3,
          api_post_user_id: settingsRes.data.api_post_user_id || '',
          submission_mode: settingsRes.data.submission_mode || 'google_form',
          confession_form_intro: settingsRes.data.confession_form_intro || '',
          confession_image_help_text: settingsRes.data.confession_image_help_text || '',
          confession_submit_button_text: settingsRes.data.confession_submit_button_text || 'Submit Confession'
        });
        setBannedDisplayNameWordsText((settingsRes.data.banned_display_name_words || []).join('\n'));
        setRules(settingsRes.data.rules || []);
        setFaqItems(settingsRes.data.faq_items || []);
        setAllUsers(usersRes.data.users || []);
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        banned_display_name_words: parseBannedDisplayNameWords(bannedDisplayNameWordsText),
      };
      await axios.put(
        `${API_URL}/api/admin/settings`,
        payload,
        
      );
      setSettings((s) => ({
        ...s,
        banned_display_name_words: payload.banned_display_name_words,
      }));
      setBannedDisplayNameWordsText(payload.banned_display_name_words.join('\n'));
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRules = async (updatedRules) => {
    setSavingRules(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/rules`,
        { rules: updatedRules },
        
      );
      setRules(updatedRules);
      toast.success('Rules saved!');
    } catch (error) {
      toast.error('Failed to save rules');
    } finally {
      setSavingRules(false);
    }
  };

  const handleAddRule = () => {
    if (!newRuleTitle.trim()) return;
    const updated = [...rules, {
      rule_id: `rule_${Date.now()}`,
      title: newRuleTitle.trim(),
      text: newRuleText.trim(),
      order: rules.length
    }];
    setNewRuleTitle('');
    setNewRuleText('');
    handleSaveRules(updated);
  };

  const handleDeleteRule = (ruleId) => {
    const updated = rules.filter(r => r.rule_id !== ruleId).map((r, i) => ({ ...r, order: i }));
    handleSaveRules(updated);
  };

  const handleMoveRule = (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= rules.length) return;
    const updated = [...rules];
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    const reordered = updated.map((r, i) => ({ ...r, order: i }));
    handleSaveRules(reordered);
  };

  const handleSaveEditRule = (ruleId) => {
    if (!editingRuleTitle.trim()) return;
    const updated = rules.map(r => r.rule_id === ruleId ? {
      ...r,
      title: editingRuleTitle.trim(),
      text: editingRuleText.trim()
    } : r);
    setEditingRuleId(null);
    setEditingRuleTitle('');
    setEditingRuleText('');
    handleSaveRules(updated);
  };

  const handleSaveFaq = async (updatedFaqItems) => {
    setSavingFaq(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/faq`,
        { faq_items: updatedFaqItems },
        
      );
      setFaqItems(updatedFaqItems);
      toast.success('FAQ saved!');
    } catch (error) {
      toast.error('Failed to save FAQ');
    } finally {
      setSavingFaq(false);
    }
  };

  const handleAddFaq = () => {
    if (!newFaqQuestion.trim()) return;
    const updated = [...faqItems, {
      faq_id: `faq_${Date.now()}`,
      question: newFaqQuestion.trim(),
      answer: newFaqAnswer.trim(),
      order: faqItems.length
    }];
    setNewFaqQuestion('');
    setNewFaqAnswer('');
    handleSaveFaq(updated);
  };

  const handleDeleteFaq = (faqId) => {
    const updated = faqItems.filter(item => item.faq_id !== faqId).map((item, i) => ({ ...item, order: i }));
    handleSaveFaq(updated);
  };

  const handleMoveFaq = (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= faqItems.length) return;
    const updated = [...faqItems];
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    const reordered = updated.map((item, i) => ({ ...item, order: i }));
    handleSaveFaq(reordered);
  };

  const handleSaveEditFaq = (faqId) => {
    if (!editingFaqQuestion.trim()) return;
    const updated = faqItems.map(item => item.faq_id === faqId ? {
      ...item,
      question: editingFaqQuestion.trim(),
      answer: editingFaqAnswer.trim()
    } : item);
    setEditingFaqId(null);
    setEditingFaqQuestion('');
    setEditingFaqAnswer('');
    handleSaveFaq(updated);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div>
        <h2 className="font-serif text-2xl font-bold">Site Settings</h2>
        <p className="text-muted-foreground">Configure your Secrets Club</p>
      </div>

      {/* Google Form URL */}
      <Card>
        <CardHeader>
          <CardTitle>Confession Form</CardTitle>
          <CardDescription>
            Configure whether submissions go to Google Forms or the new native confession page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="submissionMode">Submission Mode</Label>
            <Select
              value={settings.submission_mode || 'google_form'}
              onValueChange={(value) => setSettings(s => ({ ...s, submission_mode: value }))}
            >
              <SelectTrigger id="submissionMode" data-testid="submission-mode-select">
                <SelectValue placeholder="Select a mode..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="google_form">Google Form</SelectItem>
                <SelectItem value="native_encrypted">Native encrypted form</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Use `Both` to keep the native form as the primary flow while still offering Google Forms as a fallback on the submission page.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="googleForm">Google Form URL</Label>
            <Input
              id="googleForm"
              value={settings.google_form_url}
              onChange={(e) => setSettings(s => ({ ...s, google_form_url: e.target.value }))}
              placeholder="https://docs.google.com/forms/d/e/..."
              data-testid="google-form-url-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confessionFormIntro">Native Form Intro</Label>
            <Textarea
              id="confessionFormIntro"
              value={settings.confession_form_intro}
              onChange={(e) => setSettings(s => ({ ...s, confession_form_intro: e.target.value }))}
              placeholder="Tell members what this form is for and how their confession will be handled."
              className="min-h-[120px]"
              data-testid="confession-form-intro-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confessionImageHelpText">Image URL Help Text</Label>
            <Textarea
              id="confessionImageHelpText"
              value={settings.confession_image_help_text}
              onChange={(e) => setSettings(s => ({ ...s, confession_image_help_text: e.target.value }))}
              placeholder="Explain what the image URL field is for."
              className="min-h-[90px]"
              data-testid="confession-image-help-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confessionSubmitButtonText">Submit Button Label</Label>
            <Input
              id="confessionSubmitButtonText"
              value={settings.confession_submit_button_text}
              onChange={(e) => setSettings(s => ({ ...s, confession_submit_button_text: e.target.value }))}
              placeholder="Submit Confession"
              data-testid="confession-submit-button-text-input"
            />
          </div>
          <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-confession-form-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Confession Form Settings
          </Button>
        </CardContent>
      </Card>

      {/* About Text */}
      <Card>
        <CardHeader>
          <CardTitle>About Page</CardTitle>
          <CardDescription>
            Text displayed on the About page. Use [heading]Heading text[/heading] for white section headings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="aboutText">About Text</Label>
            <Textarea
              id="aboutText"
              value={settings.about_text}
              onChange={(e) => setSettings(s => ({ ...s, about_text: e.target.value }))}
              placeholder="Write about your community..."
              className="min-h-[150px]"
              data-testid="about-text-input"
            />
          </div>
          <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Settings
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Info Pages</CardTitle>
          <CardDescription>
            Editable text for long-form Info menu pages. Use [heading]Heading text[/heading] for white section headings; blank pages show a temporary prepared placeholder.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {[
            ['how_it_works_text', 'How It Works'],
            ['moderation_policy_text', 'Moderation Policy'],
            ['privacy_text', 'Privacy Policy'],
            ['terms_text', 'Terms of Service'],
            ['contact_text', 'Contact'],
          ].map(([field, label]) => (
            <div key={field} className="space-y-2">
              <Label htmlFor={field}>{label}</Label>
              <Textarea
                id={field}
                value={settings[field] || ''}
                onChange={(e) => setSettings(s => ({ ...s, [field]: e.target.value }))}
                placeholder={`Write the ${label} page content...`}
                className="min-h-[130px]"
                data-testid={`${field.replace(/_/g, '-')}-input`}
              />
            </div>
          ))}
          <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-info-pages-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Info Pages
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Display Name Filters</CardTitle>
          <CardDescription>
            Reserved staff words are blocked automatically. Add one extra banned word or phrase per line here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bannedDisplayNameWords">Additional banned words</Label>
            <Textarea
              id="bannedDisplayNameWords"
              value={bannedDisplayNameWordsText}
              onChange={(e) => setBannedDisplayNameWordsText(e.target.value)}
              placeholder={'nazi\nslur\nvulgar term'}
              className="min-h-[140px]"
              data-testid="banned-display-name-words-input"
            />
            <p className="text-xs text-muted-foreground">
              Built-in blocked standalone words: `Admin`, `Administrator`, `Mod`, and `Moderator`.
            </p>
          </div>
          <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-display-name-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Name Filters
          </Button>
        </CardContent>
      </Card>

      {/* Ad Frequency Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Ad Display Frequency</CardTitle>
          <CardDescription>
            Control how often ads are shown between posts for different user types. Set to 0 to disable ads for that group.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="adFreqGuest">Guest (every N posts)</Label>
              <Input
                id="adFreqGuest"
                type="number"
                min="0"
                max="50"
                value={settings.ad_frequency_guest}
                onChange={(e) => setSettings(s => ({ ...s, ad_frequency_guest: parseInt(e.target.value) || 0 }))}
                data-testid="ad-freq-guest-input"
              />
              <p className="text-xs text-muted-foreground">Show an ad every {settings.ad_frequency_guest || 'N/A'} post(s) for guests</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adFreqMember">Member (every N posts)</Label>
              <Input
                id="adFreqMember"
                type="number"
                min="0"
                max="50"
                value={settings.ad_frequency_member}
                onChange={(e) => setSettings(s => ({ ...s, ad_frequency_member: parseInt(e.target.value) || 0 }))}
                data-testid="ad-freq-member-input"
              />
              <p className="text-xs text-muted-foreground">Show an ad every {settings.ad_frequency_member || 'N/A'} post(s) for members</p>
            </div>
          </div>
          <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-ad-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Ad Settings
          </Button>
        </CardContent>
      </Card>

      {/* External API Settings */}
      <Card>
        <CardHeader>
          <CardTitle>External Post API</CardTitle>
          <CardDescription>
            Configure which user external API posts appear as. Posts created via the API will be attributed to this user.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">API Post User</label>
            <Select
              value={settings.api_post_user_id || '_none'}
              onValueChange={(v) => setSettings(s => ({ ...s, api_post_user_id: v === '_none' ? '' : v }))}
            >
              <SelectTrigger data-testid="api-post-user-select">
                <SelectValue placeholder="Select a user..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Not configured</SelectItem>
                {allUsers.map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.display_name || u.username} ({u.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">External API posts will appear as this user</p>
          </div>
          <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-api-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save API Settings
          </Button>
        </CardContent>
      </Card>

      {/* Rules Management */}
      <Card>
        <CardHeader>
          <CardTitle>Rules</CardTitle>
          <CardDescription>
            Manage the ordered list of rules displayed on the Rules page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {rules.length > 0 ? (
            <div className="space-y-2">
              {rules.map((rule, index) => (
                <div key={rule.rule_id} className="flex items-start gap-2 p-3 border border-border rounded-lg bg-card" data-testid={`admin-rule-${index}`}>
                  <span className="text-sm font-medium text-muted-foreground w-6 text-center">{index + 1}.</span>
                  
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveRule(index, -1)} disabled={index === 0 || savingRules} data-testid={`rule-move-up-${index}`}>
                      <ChevronRight className="w-4 h-4 -rotate-90" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveRule(index, 1)} disabled={index === rules.length - 1 || savingRules} data-testid={`rule-move-down-${index}`}>
                      <ChevronRight className="w-4 h-4 rotate-90" />
                    </Button>
                  </div>
                  
                  {editingRuleId === rule.rule_id ? (
                    <div className="flex-1 space-y-2">
                      <Input
                        value={editingRuleTitle}
                        onChange={(e) => setEditingRuleTitle(e.target.value)}
                        placeholder="Rule title"
                        autoFocus
                      />
                      <Textarea
                        value={editingRuleText}
                        onChange={(e) => setEditingRuleText(e.target.value)}
                        placeholder="Rule description"
                        className="min-h-[90px]"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleSaveEditRule(rule.rule_id)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingRuleId(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-semibold">{rule.title || rule.text}</p>
                        {rule.title && rule.text ? (
                          <p className="text-sm text-muted-foreground">{rule.text}</p>
                        ) : null}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingRuleId(rule.rule_id); setEditingRuleTitle(rule.title || rule.text || ''); setEditingRuleText(rule.title ? (rule.text || '') : ''); }} data-testid={`rule-edit-${index}`}>
                          <PenSquare className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDeleteRule(rule.rule_id)} data-testid={`rule-delete-${index}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No rules added yet.</p>
          )}
          
          <div className="space-y-2 pt-2 border-t border-border">
            <Input
              value={newRuleTitle}
              onChange={(e) => setNewRuleTitle(e.target.value)}
              placeholder="Rule title, e.g. No illegal content"
              data-testid="new-rule-title-input"
            />
            <Textarea
              value={newRuleText}
              onChange={(e) => setNewRuleText(e.target.value)}
              placeholder="Rule description..."
              className="min-h-[90px]"
              data-testid="new-rule-input"
            />
            <Button onClick={handleAddRule} disabled={savingRules || !newRuleTitle.trim()} data-testid="add-rule-btn">
              {savingRules ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              <span className="ml-2">Add Rule</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>FAQ</CardTitle>
          <CardDescription>
            Manage ordered FAQ questions and answers displayed on the FAQ page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {faqItems.length > 0 ? (
            <div className="space-y-2">
              {faqItems.map((item, index) => (
                <div key={item.faq_id} className="flex items-start gap-2 p-3 border border-border rounded-lg bg-card" data-testid={`admin-faq-${index}`}>
                  <span className="text-sm font-medium text-muted-foreground w-6 text-center">{index + 1}.</span>

                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveFaq(index, -1)} disabled={index === 0 || savingFaq} data-testid={`faq-move-up-${index}`}>
                      <ChevronRight className="w-4 h-4 -rotate-90" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveFaq(index, 1)} disabled={index === faqItems.length - 1 || savingFaq} data-testid={`faq-move-down-${index}`}>
                      <ChevronRight className="w-4 h-4 rotate-90" />
                    </Button>
                  </div>

                  {editingFaqId === item.faq_id ? (
                    <div className="flex-1 space-y-2">
                      <Input
                        value={editingFaqQuestion}
                        onChange={(e) => setEditingFaqQuestion(e.target.value)}
                        placeholder="Question"
                        autoFocus
                      />
                      <Textarea
                        value={editingFaqAnswer}
                        onChange={(e) => setEditingFaqAnswer(e.target.value)}
                        placeholder="Answer"
                        className="min-h-[90px]"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleSaveEditFaq(item.faq_id)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingFaqId(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-semibold">{item.question}</p>
                        <p className="text-sm text-muted-foreground">{item.answer}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingFaqId(item.faq_id); setEditingFaqQuestion(item.question || ''); setEditingFaqAnswer(item.answer || ''); }} data-testid={`faq-edit-${index}`}>
                          <PenSquare className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDeleteFaq(item.faq_id)} data-testid={`faq-delete-${index}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No FAQ items added yet.</p>
          )}

          <div className="space-y-2 pt-2 border-t border-border">
            <Input
              value={newFaqQuestion}
              onChange={(e) => setNewFaqQuestion(e.target.value)}
              placeholder="Question"
              data-testid="new-faq-question-input"
            />
            <Textarea
              value={newFaqAnswer}
              onChange={(e) => setNewFaqAnswer(e.target.value)}
              placeholder="Answer"
              className="min-h-[90px]"
              data-testid="new-faq-answer-input"
            />
            <Button onClick={handleAddFaq} disabled={savingFaq || !newFaqQuestion.trim()} data-testid="add-faq-btn">
              {savingFaq ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              <span className="ml-2">Add FAQ</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Reports Tab - Manage reported comments
const ApprovalsTab = () => {
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  const fetchPendingComments = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/comments/pending`);
      setComments(response.data.comments || []);
    } catch (error) {
      toast.error('Failed to load pending comments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPendingComments(); }, [fetchPendingComments]);

  const handleApprove = async (commentId, endProbation = false) => {
    const key = `${commentId}:${endProbation ? 'preapprove' : 'approve'}`;
    setActionLoading(key);
    try {
      await axios.put(`${API_URL}/api/comments/${commentId}/approve?end_probation=${endProbation}`);
      toast.success(endProbation ? 'Comment approved and probation ended' : 'Comment approved');
      fetchPendingComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve comment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async () => {
    if (!declineTarget) return;
    setActionLoading(`${declineTarget.comment_id}:decline`);
    try {
      await axios.put(`${API_URL}/api/comments/${declineTarget.comment_id}/decline`, {
        reason: declineReason.trim() || null,
      });
      toast.success('Comment declined');
      setDeclineTarget(null);
      setDeclineReason('');
      fetchPendingComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline comment');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-approvals">
      <div>
        <h2 className="font-serif text-2xl font-bold">Comment Approvals</h2>
        <p className="text-muted-foreground">Review probationary comments before they go live.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
        </div>
      ) : comments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No comments are waiting for approval</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {comments.map(comment => {
            const approveKey = `${comment.comment_id}:approve`;
            const preapproveKey = `${comment.comment_id}:preapprove`;
            const declineKey = `${comment.comment_id}:decline`;
            return (
              <Card key={comment.comment_id} data-testid={`approval-${comment.comment_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge variant="outline" className="text-blue-600 border-blue-300">
                          Pending Approval
                        </Badge>
                        {comment.is_on_probation && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            Probationary Member
                          </Badge>
                        )}
                        {comment.is_anonymous && (
                          <Badge variant="secondary">Anonymous</Badge>
                        )}
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3 mb-3 border-l-4 border-blue-300">
                        <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          By: <strong>{comment.reported_display_name || comment.display_name}</strong> ({comment.reported_username})
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                        <span>Post: <strong>{comment.post_tag || 'Unknown'}</strong></span>
                        <span>{new Date(comment.created_at).toLocaleString()}</span>
                        {comment.parent_comment_snippet && <span>Replying to: "{comment.parent_comment_snippet}"</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                      onClick={() => handleApprove(comment.comment_id, false)}
                      disabled={actionLoading === approveKey}
                      data-testid={`approval-approve-${comment.comment_id}`}
                    >
                      {actionLoading === approveKey ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-blue-600 border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      onClick={() => handleApprove(comment.comment_id, true)}
                      disabled={actionLoading === preapproveKey}
                      data-testid={`approval-preapprove-${comment.comment_id}`}
                    >
                      {actionLoading === preapproveKey ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
                      Pre-Approve Member
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={() => {
                        setDeclineTarget(comment);
                        setDeclineReason('');
                      }}
                      disabled={actionLoading === declineKey}
                      data-testid={`approval-decline-${comment.comment_id}`}
                    >
                      {actionLoading === declineKey ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      Decline
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/post/${comment.post_id}`)}
                      data-testid={`approval-view-${comment.comment_id}`}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> View Post
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!declineTarget} onOpenChange={(open) => {
        if (!open) {
          setDeclineTarget(null);
          setDeclineReason('');
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Decline Comment</DialogTitle>
            <DialogDescription>
              This comment will not be posted. The member will receive the full reason you provide here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="decline-comment-reason">Reason</Label>
            <Textarea
              id="decline-comment-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Explain why this comment was declined."
              rows={6}
              data-testid="decline-comment-reason"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setDeclineTarget(null);
              setDeclineReason('');
            }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDecline}
              disabled={!declineReason.trim() || actionLoading === `${declineTarget?.comment_id}:decline`}
              data-testid="confirm-decline-comment"
            >
              {actionLoading === `${declineTarget?.comment_id}:decline` ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Decline Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ReportsTab = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [showMuteDialog, setShowMuteDialog] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [targetReport, setTargetReport] = useState(null);
  const [muteDays, setMuteDays] = useState('1');
  const [muteReason, setMuteReason] = useState('');
  const [banReason, setBanReason] = useState('');

  const fetchReports = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/reports?status=${filter}&limit=50`);
      setReports(response.data.reports);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const getActionSuccessMessage = (action) => {
    switch (action) {
      case 'keep':
        return 'Report dismissed';
      case 'hide':
        return 'Comment hidden';
      case 'delete':
        return 'Comment deleted';
      case 'force-change':
        return 'Username reset triggered';
      default:
        return 'Report updated';
    }
  };

  const handleAction = async (reportId, action) => {
    setActionLoading(reportId + action);
    try {
      await axios.post(
        `${API_URL}/api/admin/reports/${reportId}/${action}`,
        action === 'force-change' ? {} : undefined,
      );
      toast.success(getActionSuccessMessage(action));
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMuteFromReport = async () => {
    if (!targetReport) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${targetReport.reported_user_id}/mute`,
        { days: parseInt(muteDays), reason: muteReason || null },
        
      );
      toast.success(`User muted for ${muteDays} day(s)`);
      setShowMuteDialog(false);
      setMuteReason('');
      setTargetReport(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mute user');
    }
  };

  const handleBanFromReport = async () => {
    if (!targetReport) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${targetReport.reported_user_id}/ban`,
        { reason: banReason || null },
        
      );
      toast.success('User banned permanently');
      setShowBanDialog(false);
      setBanReason('');
      setTargetReport(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div>
        <h2 className="font-serif text-2xl font-bold">Reports</h2>
        <p className="text-muted-foreground">Review and act on reported comments and usernames</p>
      </div>

      <div className="flex gap-2 items-center">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {['pending', 'resolved', 'all'].map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            data-testid={`report-filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)} {f === 'pending' && total > 0 && filter === 'pending' ? `(${total})` : ''}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Flag className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No {filter === 'all' ? '' : filter} reports</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map(report => {
            const isUsernameReport = report.type === 'username';
            return (
              <Card key={report.report_id} className={report.status === 'resolved' ? 'opacity-60' : ''} data-testid={`report-${report.report_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge variant="outline" className="text-red-600 border-red-300">
                          <Flag className="w-3 h-3 mr-1" /> Reported
                        </Badge>
                        <Badge variant="outline">
                          {isUsernameReport ? 'Username' : 'Comment'}
                        </Badge>
                        {report.status === 'resolved' && (
                          <Badge variant="secondary">{report.resolution || 'resolved'}</Badge>
                        )}
                        {report.comment_is_hidden && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">Hidden</Badge>
                        )}
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3 mb-3 border-l-4 border-red-300">
                        <p className="text-sm whitespace-pre-wrap">{report.comment_content}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {isUsernameReport ? 'Member' : 'By'}: <strong>{report.reported_username}</strong>
                          {!isUsernameReport && report.comment_is_anonymous && ' (anonymous)'}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Reported by: <strong>{report.reporter_username}</strong></span>
                        {report.reason && <span>Reason: "{report.reason}"</span>}
                        <span>{new Date(report.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  {report.status === 'pending' && (
                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
                      <Button
                        size="sm" variant="outline"
                        className="text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                        onClick={() => handleAction(report.report_id, 'keep')}
                        disabled={actionLoading === report.report_id + 'keep'}
                        data-testid={`report-keep-${report.report_id}`}
                      >
                        {actionLoading === report.report_id + 'keep' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                        Keep
                      </Button>
                      {isUsernameReport ? (
                        <Button
                          size="sm" variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                          onClick={() => handleAction(report.report_id, 'force-change')}
                          disabled={actionLoading === report.report_id + 'force-change'}
                          data-testid={`report-force-change-${report.report_id}`}
                        >
                          {actionLoading === report.report_id + 'force-change' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Force Change
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm" variant="outline"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                            onClick={() => handleAction(report.report_id, 'hide')}
                            disabled={actionLoading === report.report_id + 'hide'}
                            data-testid={`report-hide-${report.report_id}`}
                          >
                            {actionLoading === report.report_id + 'hide' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                            Hide
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => handleAction(report.report_id, 'delete')}
                            disabled={actionLoading === report.report_id + 'delete'}
                            data-testid={`report-delete-${report.report_id}`}
                          >
                            {actionLoading === report.report_id + 'delete' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                            Delete
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm" variant="outline"
                        className="text-amber-600 border-amber-300"
                        onClick={() => { setTargetReport(report); setShowMuteDialog(true); }}
                        data-testid={`report-mute-${report.report_id}`}
                      >
                        <VolumeX className="w-3 h-3 mr-1" /> Mute Member
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="text-red-600 border-red-300"
                        onClick={() => { setTargetReport(report); setShowBanDialog(true); }}
                        data-testid={`report-ban-${report.report_id}`}
                      >
                        <Ban className="w-3 h-3 mr-1" /> Ban Member
                      </Button>
                      {!isUsernameReport && report.post_id && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => navigate(`/post/${report.post_id}`)}
                          data-testid={`report-view-${report.report_id}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" /> View In Post
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Mute Dialog */}
      <Dialog open={showMuteDialog} onOpenChange={setShowMuteDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Mute {targetReport?.reported_username}</DialogTitle>
            <DialogDescription>Muted users cannot comment or react.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-1 block">Duration</Label>
              <Select value={muteDays} onValueChange={setMuteDays}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,3,7,14,28].map(d => <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Reason (optional)</Label>
              <Input value={muteReason} onChange={(e) => setMuteReason(e.target.value)} placeholder="Reason..." />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowMuteDialog(false)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleMuteFromReport}>Mute</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban Dialog */}
      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Ban {targetReport?.reported_username}?</DialogTitle>
            <DialogDescription>Permanently prevents commenting and reacting.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium mb-1 block">Reason (optional)</Label>
            <Input value={banReason} onChange={(e) => setBanReason(e.target.value)} placeholder="Reason..." />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBanDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBanFromReport}>Ban Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Security Dashboard
const SecurityTab = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [securityData, setSecurityData] = useState(null);
  const [bannedIps, setBannedIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [newBanIp, setNewBanIp] = useState('');
  const [newBanIpReason, setNewBanIpReason] = useState('');
  const [encryptionStatus, setEncryptionStatus] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [auditLookupInput, setAuditLookupInput] = useState('');
  const [auditLookupLoading, setAuditLookupLoading] = useState(false);
  const [auditLookupResult, setAuditLookupResult] = useState(null);

  const isSuperadmin = currentUser?.role === 'superadmin';

  const fetchEncryptionStatus = React.useCallback(async () => {
    if (!isSuperadmin) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/encryption/status`);
      setEncryptionStatus(res.data);
    } catch {
      // silently fail for non-superadmins
    }
  }, [isSuperadmin]);

  const handleRotateKey = async () => {
    setShowRotateConfirm(false);
    setRotating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/encryption/rotate`);
      toast.success(`Key rotated successfully. ${res.data.users_rotated} users re-encrypted.`);
      fetchEncryptionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Key rotation failed');
    } finally {
      setRotating(false);
    }
  };

  const fetchSecurityData = React.useCallback(async () => {
    try {
      const [secRes, ipRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/security?limit=100`),
        axios.get(`${API_URL}/api/admin/security/banned-ips`)
      ]);
      setSecurityData(secRes.data);
      setBannedIps(ipRes.data.banned_ips || []);
    } catch (error) {
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAuditLookup = async () => {
    const publicKey = auditLookupInput.trim().toUpperCase();
    if (!publicKey) {
      toast.error('Enter the submission public key from the spreadsheet first.');
      return;
    }

    setAuditLookupLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/confession-audit/lookup`, {
        params: { public_key: publicKey },
      });
      setAuditLookupResult(res.data);
    } catch (error) {
      setAuditLookupResult(null);
      toast.error(error.response?.data?.detail || 'Failed to look up that submission code.');
    } finally {
      setAuditLookupLoading(false);
    }
  };

  useEffect(() => {
    fetchSecurityData();
    fetchEncryptionStatus();
  }, [fetchEncryptionStatus, fetchSecurityData]);

  const handleClearLogs = async () => {
    if (!window.confirm('Clear all tamper logs? This cannot be undone.')) return;
    setClearing(true);
    try {
      await axios.delete(`${API_URL}/api/admin/security/logs`);
      toast.success('Tamper logs cleared');
      fetchSecurityData();
    } catch (error) {
      toast.error('Failed to clear logs');
    } finally {
      setClearing(false);
    }
  };

  const handleBanIp = async () => {
    if (!newBanIp.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/security/ban-ip`,
        { ip: newBanIp.trim(), reason: newBanIpReason.trim() || null },
        
      );
      toast.success(`IP ${newBanIp} banned`);
      setNewBanIp('');
      setNewBanIpReason('');
      fetchSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban IP');
    }
  };

  const handleUnbanIp = async (ip) => {
    if (!window.confirm(`Unban IP ${ip}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/security/unban-ip/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} unbanned`);
      fetchSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban IP');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
      </div>
    );
  }

  const stats = securityData?.stats || {};
  const logs = securityData?.tamper_logs || [];
  const offenders = securityData?.top_offenders || [];
  const typeBreakdown = securityData?.event_type_breakdown || {};

  return (
    <div className="space-y-6" data-testid="admin-security">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="font-serif text-2xl font-bold">Security Dashboard</h2>
          <p className="text-muted-foreground">Monitor tampering attempts, rate limits & suspicious activity</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchSecurityData}
          data-testid="refresh-security-btn"
        >
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Events (24h)</p>
                <p className="text-3xl font-bold">{stats.events_24h || 0}</p>
              </div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${stats.events_24h > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                <AlertTriangle className={`w-6 h-6 ${stats.events_24h > 0 ? 'text-red-600' : 'text-green-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Events (7 days)</p>
                <p className="text-3xl font-bold">{stats.events_7d || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {stats.unique_offenders_7d || 0} unique users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Security Bans</p>
                <p className="text-3xl font-bold">{stats.security_banned_users || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {stats.banned_ips || 0} IPs blocked
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Banned Users</p>
                <p className="text-3xl font-bold">{stats.banned_users || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Ban className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Muted Users</p>
                <p className="text-3xl font-bold">{stats.muted_users || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <VolumeX className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Encryption Key Rotation — Superadmin Only */}
      {isSuperadmin && encryptionStatus && (
        <Card data-testid="encryption-key-rotation-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Encryption Key Rotation</CardTitle>
                <CardDescription>AES-256 at-rest encryption for emails and real names</CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEncryptionStatus}
              data-testid="refresh-encryption-status-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Last Rotated</p>
                <p className="text-sm font-semibold" data-testid="last-rotated-date">
                  {encryptionStatus.last_rotated_at
                    ? format(new Date(encryptionStatus.last_rotated_at), 'MMM d, yyyy HH:mm')
                    : 'Never'}
                </p>
                {encryptionStatus.last_triggered_by && (
                  <p className="text-xs text-muted-foreground mt-1">
                    by {encryptionStatus.last_triggered_by}
                  </p>
                )}
              </div>
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Next Scheduled</p>
                <p className="text-sm font-semibold" data-testid="next-rotation-date">
                  {encryptionStatus.next_scheduled_rotation
                    ? format(new Date(encryptionStatus.next_scheduled_rotation), 'MMM d, yyyy')
                    : 'Not scheduled'}
                </p>
                {encryptionStatus.days_since_last_rotation !== null && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {encryptionStatus.rotation_interval_days - encryptionStatus.days_since_last_rotation > 0
                      ? `${encryptionStatus.rotation_interval_days - encryptionStatus.days_since_last_rotation} days remaining`
                      : 'Overdue'}
                  </p>
                )}
              </div>
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Encrypted Users</p>
                <p className="text-sm font-semibold" data-testid="encrypted-users-count">
                  {encryptionStatus.encrypted_users} / {encryptionStatus.total_users}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {encryptionStatus.total_users > 0
                    ? `${Math.round((encryptionStatus.encrypted_users / encryptionStatus.total_users) * 100)}% coverage`
                    : 'No users'}
                </p>
              </div>
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Auto-Rotation</p>
                <p className="text-sm font-semibold">Every {encryptionStatus.rotation_interval_days} days</p>
                <p className="text-xs text-muted-foreground mt-1">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Checked on each server start
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowRotateConfirm(true)}
                disabled={rotating || encryptionStatus.is_rotating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="rotate-key-btn"
              >
                {rotating || encryptionStatus.is_rotating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Rotating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Rotate Key Now
                  </>
                )}
              </Button>
              {encryptionStatus.last_errors > 0 && (
                <Badge variant="destructive" data-testid="rotation-errors-badge">
                  {encryptionStatus.last_errors} error(s) in last rotation
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rotate Key Confirmation Dialog */}
      <Dialog open={showRotateConfirm} onOpenChange={setShowRotateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rotate Encryption Key</DialogTitle>
            <DialogDescription>
              This will generate a new AES-256 encryption key and re-encrypt all user emails
              and real names. The process may take a moment for large datasets.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground space-y-2">
            <p>This action will:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Generate a new encryption key</li>
              <li>Re-encrypt {encryptionStatus?.total_users || 0} user records</li>
              <li>Recompute all blind indexes for email lookups</li>
              <li>Update the server's encryption key</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRotateConfirm(false)} data-testid="cancel-rotate-btn">
              Cancel
            </Button>
            <Button
              onClick={handleRotateKey}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-rotate-btn"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Confirm Rotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isSuperadmin && (
        <Card data-testid="confession-audit-lookup">
          <CardHeader>
            <CardTitle className="text-lg">Confession Audit Lookup</CardTitle>
            <CardDescription>
              Superadmin-only lookup for the submission `public_key` value stored in the spreadsheet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                placeholder="Enter spreadsheet public_key"
                value={auditLookupInput}
                onChange={(e) => setAuditLookupInput(e.target.value.toUpperCase())}
                className="sm:max-w-xs"
                data-testid="confession-audit-input"
              />
              <Button
                onClick={handleAuditLookup}
                disabled={auditLookupLoading || !auditLookupInput.trim()}
                data-testid="confession-audit-lookup-btn"
              >
                {auditLookupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Look Up Submission
              </Button>
            </div>

            {auditLookupResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Linked Account</p>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-muted-foreground">Display name:</span> {auditLookupResult.user.display_name || '-'}</p>
                      <p><span className="text-muted-foreground">Username:</span> {auditLookupResult.user.username || '-'}</p>
                      <p><span className="text-muted-foreground">Email:</span> {auditLookupResult.user.email || '-'}</p>
                      <p><span className="text-muted-foreground">Role:</span> {auditLookupResult.user.role || '-'}</p>
                      <p><span className="text-muted-foreground">Verified:</span> {auditLookupResult.user.email_verified ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="pt-2">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/profile/${auditLookupResult.user.user_id}`)}>
                        View Profile
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Lookup Details</p>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-muted-foreground">public_key:</span> <span className="font-mono">{auditLookupResult.public_key}</span></p>
                      <p><span className="text-muted-foreground">Submissions logged:</span> {auditLookupResult.submission_count}</p>
                      <p><span className="text-muted-foreground">Account created:</span> {auditLookupResult.user.created_at ? format(new Date(auditLookupResult.user.created_at), 'MMM d, yyyy HH:mm') : '-'}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium">Recent Submission Audits</p>
                    <Badge variant="secondary">{auditLookupResult.audits.length}</Badge>
                  </div>
                  {auditLookupResult.audits.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No submission audits recorded yet for this public key.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Time</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Relay</TableHead>
                            <TableHead>Length</TableHead>
                            <TableHead>Image</TableHead>
                            <TableHead>IP</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditLookupResult.audits.map((audit) => (
                            <TableRow key={audit.audit_id}>
                              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                {audit.created_at ? format(new Date(audit.created_at), 'MMM d, HH:mm:ss') : '-'}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{audit.status || '-'}</Badge>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">{audit.relay_result || '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{audit.confession_length ?? '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{audit.has_image_link ? 'Yes' : 'No'}</TableCell>
                              <TableCell className="font-mono text-xs text-muted-foreground">{audit.ip_address || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Event Type Breakdown + Top Offenders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Event Type Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Event Types (7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(typeBreakdown).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No events recorded</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(typeBreakdown).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        type === 'overlay_removed' ? 'bg-red-500' :
                        type === 'style_tampered' ? 'bg-orange-500' :
                        type === 'overlay_modified' ? 'bg-amber-500' :
                        'bg-gray-400'
                      }`} />
                      <span className="text-sm font-mono">{type}</span>
                    </div>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Offenders */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Offenders (7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {offenders.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No offenders detected</p>
            ) : (
              <div className="space-y-3">
                {offenders.map((offender) => (
                  <div key={offender.user_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => navigate(`/profile/${offender.user_id}`)}
                        className="text-sm font-medium hover:text-forest-700 dark:hover:text-forest-400 transition-colors"
                        data-testid={`offender-${offender.user_id}`}
                      >
                        {offender.display_name}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={offender.count >= 5 ? 'destructive' : 'secondary'}>
                        {offender.count} event{offender.count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tamper Log Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Tamper Log ({stats.total_events || 0} total)</CardTitle>
            <CardDescription>All recorded tampering and bypass attempts</CardDescription>
          </div>
          {logs.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={handleClearLogs}
              disabled={clearing}
              data-testid="clear-logs-btn"
            >
              {clearing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Clear All
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-10">
              <ShieldAlert className="w-12 h-12 mx-auto text-green-500/40 mb-4" />
              <p className="text-muted-foreground">No tampering events recorded. All clear!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.log_id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(log.created_at), 'MMM d, HH:mm:ss')}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/profile/${log.user_id}`)}
                          className="text-sm font-medium hover:text-forest-700 dark:hover:text-forest-400 transition-colors"
                        >
                          {log.display_name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          log.event_type === 'overlay_removed' ? 'destructive' :
                          log.event_type === 'style_tampered' ? 'default' : 'secondary'
                        } className="font-mono text-xs">
                          {log.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                        {log.details || '-'}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {log.ip || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Banned IPs Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Banned IPs ({bannedIps.length})</CardTitle>
          <CardDescription>IP addresses blocked from accessing the site. Only superadmin can manage.</CardDescription>
        </CardHeader>
        <CardContent>
          {isSuperadmin && (
            <div className="flex gap-2 mb-4" data-testid="ban-ip-form">
              <Input
                placeholder="IP address (e.g. 192.168.1.1)"
                value={newBanIp}
                onChange={(e) => setNewBanIp(e.target.value)}
                className="max-w-xs"
                data-testid="ban-ip-input"
              />
              <Input
                placeholder="Reason (optional)"
                value={newBanIpReason}
                onChange={(e) => setNewBanIpReason(e.target.value)}
                className="max-w-xs"
                data-testid="ban-ip-reason-input"
              />
              <Button size="sm" onClick={handleBanIp} disabled={!newBanIp.trim()} data-testid="ban-ip-btn">
                <Ban className="w-3 h-3 mr-1" /> Ban IP
              </Button>
            </div>
          )}
          {bannedIps.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No IPs currently banned</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>IP Address</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Banned At</TableHead>
                    {isSuperadmin && <TableHead>Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bannedIps.map((ban) => (
                    <TableRow key={ban.ip}>
                      <TableCell className="font-mono text-sm">{ban.ip}</TableCell>
                      <TableCell>
                        {ban.user_id ? (
                          <button
                            onClick={() => navigate(`/profile/${ban.user_id}`)}
                            className="text-sm hover:text-forest-700 dark:hover:text-forest-400 transition-colors"
                          >
                            {ban.display_name || 'Unknown'}
                          </button>
                        ) : (
                          <span className="text-muted-foreground text-sm">{ban.display_name || '-'}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{ban.reason || '-'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {ban.created_at ? format(new Date(ban.created_at), 'MMM d, HH:mm') : '-'}
                      </TableCell>
                      {isSuperadmin && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => handleUnbanIp(ban.ip)}
                            data-testid={`unban-ip-${ban.ip}`}
                          >
                            Unban
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Rate Limit Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Active Rate Limits</CardTitle>
          <CardDescription>Current rate limiting configuration for critical endpoints</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Limit</TableHead>
                  <TableHead>Purpose</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { endpoint: 'POST /api/auth/register', limit: '5/minute', purpose: 'Prevent spam registrations' },
                  { endpoint: 'POST /api/auth/login', limit: '10/minute', purpose: 'Prevent brute-force attacks' },
                  { endpoint: 'POST /api/posts/{id}/comments', limit: '20/minute', purpose: 'Prevent comment flooding' },
                  { endpoint: 'POST /api/comments/{id}/reactions', limit: '30/minute', purpose: 'Prevent reaction spam' },
                  { endpoint: 'POST /api/comments/{id}/report', limit: '10/minute', purpose: 'Prevent report abuse' },
                  { endpoint: 'POST /api/users/report-tampering', limit: '3/minute', purpose: 'Anti-flood for tamper reports' }].map((item) => (
                  <TableRow key={item.endpoint}>
                    <TableCell className="font-mono text-xs">{item.endpoint}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.limit}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.purpose}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== THEMES TAB ==============
const ThemesTab = () => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // 'dark' or 'light'

  const emptyForm = {
    name: '', description: '',
    dark_image: '', light_image: '',
    dark_colors: { primary: '#22c55e', background: '#0a1a0e', card: '#0f2614', foreground: '#d5e8d8', muted: '#1a3a20', border: '#234a28' },
    light_colors: { primary: '#16a34a', background: '#ecf5ee', card: '#f5faf6', foreground: '#0a2e12', muted: '#d5e8d8', border: '#b8d4bc' }
  };
  const [form, setForm] = useState(emptyForm);

  const fetchThemes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/themes`);
      setThemes(res.data.themes || []);
    } catch { toast.error('Failed to load themes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchThemes(); }, []);

  const openCreate = () => {
    setEditingTheme(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (theme) => {
    setEditingTheme(theme);
    setForm({
      name: theme.name || '',
      description: theme.description || '',
      dark_image: theme.dark_image || '',
      light_image: theme.light_image || '',
      dark_colors: { primary: '#22c55e', background: '#0a1a0e', card: '#0f2614', foreground: '#d5e8d8', muted: '#1a3a20', border: '#234a28', ...(theme.dark_colors || {}) },
      light_colors: { primary: '#16a34a', background: '#ecf5ee', card: '#f5faf6', foreground: '#0a2e12', muted: '#d5e8d8', border: '#b8d4bc', ...(theme.light_colors || {}) }
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Theme name is required'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description, dark_image: form.dark_image, light_image: form.light_image, dark_colors: form.dark_colors, light_colors: form.light_colors };
      if (editingTheme) {
        await axios.put(`${API_URL}/api/admin/themes/${editingTheme.theme_id}`, payload);
        toast.success('Theme updated');
      } else {
        await axios.post(`${API_URL}/api/admin/themes`, payload);
        toast.success('Theme created');
      }
      setShowDialog(false);
      fetchThemes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save theme'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (themeId) => {
    if (!window.confirm('Delete this theme? Users with it selected will be reset to Default.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/themes/${themeId}`);
      toast.success('Theme deleted');
      fetchThemes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleImageUpload = async (e, mode) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(mode);
    const themeId = editingTheme?.theme_id;
    if (!themeId) {
      toast.error('Save the theme first, then upload images');
      setUploading(null);
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/admin/themes/${themeId}/image?mode=${mode}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, [`${mode}_image`]: res.data.url }));
      toast.success(`${mode === 'dark' ? 'Dark' : 'Light'} image uploaded`);
    } catch (err) { toast.error('Image upload failed'); }
    finally { setUploading(null); }
  };

  const updateColor = (mode, key, value) => {
    setForm(prev => ({
      ...prev,
      [`${mode}_colors`]: { ...prev[`${mode}_colors`], [key]: value }
    }));
  };

  const colorFields = [
    { key: 'primary', label: 'Primary (accent)' },
    { key: 'background', label: 'Background' },
    { key: 'card', label: 'Card' },
    { key: 'foreground', label: 'Text' },
    { key: 'muted', label: 'Muted' },
    { key: 'border', label: 'Border' }
  ];

  const ColorPicker = ({ mode, field }) => (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={form[`${mode}_colors`]?.[field.key] || '#000000'}
        onChange={(e) => updateColor(mode, field.key, e.target.value)}
        className="w-8 h-8 rounded cursor-pointer border border-border"
        data-testid={`color-${mode}-${field.key}`}
      />
      <span className="text-xs text-muted-foreground">{field.label}</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="themes-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold">Custom Themes</h2>
          <p className="text-muted-foreground text-sm">Create and manage site themes with custom colors and backgrounds</p>
        </div>
        <Button onClick={openCreate} data-testid="create-theme-btn"><Plus className="w-4 h-4 mr-1" /> Create Theme</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : themes.length === 0 ? (
        <Card className="text-center py-10"><CardContent><p className="text-muted-foreground">No themes yet.</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {themes.map(theme => (
            <Card key={theme.theme_id} data-testid={`theme-card-${theme.theme_id}`} className="overflow-hidden">
              {/* Preview images */}
              <div className="grid grid-cols-2 h-24">
                <div className="relative overflow-hidden bg-muted">
                  {theme.dark_image ? (
                    <img src={resolveThemeImageUrl(theme.dark_image)} alt="Dark" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Dark</div>
                  )}
                  <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1 rounded">Dark</span>
                </div>
                <div className="relative overflow-hidden bg-muted">
                  {theme.light_image ? (
                    <img src={resolveThemeImageUrl(theme.light_image)} alt="Light" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Light</div>
                  )}
                  <span className="absolute bottom-1 left-1 text-[10px] bg-white/60 text-black px-1 rounded">Light</span>
                </div>
              </div>
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{theme.name}</h3>
                    <p className="text-xs text-muted-foreground">{theme.description}</p>
                    {theme.is_default && <Badge variant="outline" className="text-[10px] mt-1">Built-in</Badge>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(theme)} data-testid={`edit-theme-${theme.theme_id}`}>
                      <PenSquare className="w-3.5 h-3.5" />
                    </Button>
                    {!theme.is_default && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(theme.theme_id)} data-testid={`delete-theme-${theme.theme_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Color swatches preview */}
                <div className="flex gap-1 mt-2">
                  {['primary', 'background', 'card', 'foreground', 'muted', 'border'].map(k => (
                    <div key={k} className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: theme.dark_colors?.[k] || '#333' }} title={`Dark ${k}`} />
                  ))}
                  <span className="mx-1 text-muted-foreground text-xs">/</span>
                  {['primary', 'background', 'card', 'foreground', 'muted', 'border'].map(k => (
                    <div key={k} className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: theme.light_colors?.[k] || '#ccc' }} title={`Light ${k}`} />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTheme ? 'Edit Theme' : 'Create Theme'}</DialogTitle>
            <DialogDescription>Design a custom theme with colors and background images for light and dark modes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* Name & Description */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Theme name" disabled={editingTheme?.is_default} data-testid="theme-name-input" />
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Short description" data-testid="theme-desc-input" />
              </div>
            </div>

            {/* Background Images */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Image className="w-4 h-4" /> Background Images</Label>
              <p className="text-xs text-muted-foreground">Recommended size: <strong>1536 x 1024</strong> pixels. {editingTheme ? 'Upload to replace.' : 'Save the theme first, then upload images.'}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Dark Mode</Label>
                  <div className="relative h-28 rounded-lg border border-border overflow-hidden bg-muted group">
                    {form.dark_image ? (
                      <img src={resolveThemeImageUrl(form.dark_image)} alt="Dark bg" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
                    )}
                    <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                      {uploading === 'dark' ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Upload className="w-5 h-5 text-white" />}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'dark')} disabled={!editingTheme} data-testid="upload-dark-image" />
                    </label>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Light Mode</Label>
                  <div className="relative h-28 rounded-lg border border-border overflow-hidden bg-muted group">
                    {form.light_image ? (
                      <img src={resolveThemeImageUrl(form.light_image)} alt="Light bg" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
                    )}
                    <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                      {uploading === 'light' ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Upload className="w-5 h-5 text-white" />}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'light')} disabled={!editingTheme} data-testid="upload-light-image" />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Color Pickers */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Palette className="w-4 h-4" /> Colors</Label>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Dark Mode</p>
                  <div className="grid grid-cols-2 gap-2">
                    {colorFields.map(f => <ColorPicker key={f.key} mode="dark" field={f} />)}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Light Mode</p>
                  <div className="grid grid-cols-2 gap-2">
                    {colorFields.map(f => <ColorPicker key={f.key} mode="light" field={f} />)}
                  </div>
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="grid grid-cols-2 gap-3">
                {['dark', 'light'].map(mode => (
                  <div key={mode} className="rounded-lg p-3 border border-border overflow-hidden" style={{ backgroundColor: form[`${mode}_colors`]?.background || '#000' }}>
                    <div className="rounded-lg p-3" style={{ backgroundColor: form[`${mode}_colors`]?.card || '#111' }}>
                      <p className="text-sm font-semibold" style={{ color: form[`${mode}_colors`]?.foreground || '#fff' }}>Card Title</p>
                      <p className="text-xs mt-1" style={{ color: form[`${mode}_colors`]?.muted || '#888' }}>Secondary text here</p>
                      <div className="mt-2 inline-block rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: form[`${mode}_colors`]?.primary || '#22c55e', color: '#fff' }}>
                        Button
                      </div>
                    </div>
                    <p className="text-[10px] mt-1 text-center" style={{ color: form[`${mode}_colors`]?.muted || '#888' }}>{mode} mode</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="submit-theme-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingTheme ? 'Save Changes' : 'Create Theme'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Main Admin Dashboard
export default function AdminDashboard() {
  const location = useLocation();
  const { adminTabs } = useAuth();

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-[2px]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-8">
            <h1 className="font-serif text-3xl font-bold">Admin Panel</h1>
            <p className="text-muted-foreground">Manage your Secrets Club community</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <Card className="p-4 sticky top-24 bg-black/45 border-white/10 backdrop-blur-md">
                <AdminNav currentPath={location.pathname} allowedTabs={adminTabs} />
              </Card>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              <Routes>
                <Route index element={<OverviewTab />} />
                <Route path="posts" element={<PostsTab />} />
                <Route path="topics" element={<TopicsTab />} />
                <Route path="users" element={<UsersTab />} />
                <Route path="roles" element={<RolesTab />} />
                <Route path="badges" element={<BadgesTab />} />
                <Route path="themes" element={<ThemesTab />} />
                <Route path="approvals" element={<ApprovalsTab />} />
                <Route path="reports" element={<ReportsTab />} />
                <Route path="security" element={<SecurityTab />} />
                <Route path="settings" element={<SiteSettingsTab />} />
              </Routes>
            </main>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
